import React, { useCallback, useEffect, useRef, useState } from "react";
import { C, DOC_TYPE_LABELS } from "../constants.js";
import { I } from "../icons.jsx";
import { API, apiFetch, fetchMatterProviders } from "../api.js";
import { Badge } from "../components/Badge.jsx";
import { Spinner } from "../components/Spinner.jsx";
import { TabPanel } from "../components/TabPanel.jsx";
// Matter-detail tab cards. These were extracted into matter-detail/
// in slice 4 of the refactor; MatterDetailPage references them by
// name inside its tab-panel rendering so we need explicit imports
// to avoid "X is not defined" runtime errors. The earlier extraction
// script missed component-name references (it only scanned for
// hooks + constants + helpers).
import { StageTracker } from "../matter-detail/StageTracker.jsx";
import { StagePackageCard } from "../matter-detail/StagePackageCard.jsx";
import { StageDocumentsCard } from "../matter-detail/StageDocumentsCard.jsx";
import { LiensCard } from "../matter-detail/LiensCard.jsx";
import { CaseCostsCard } from "../matter-detail/CaseCostsCard.jsx";
import { SettlementCard } from "../matter-detail/SettlementCard.jsx";
import { FeeAgreementCard } from "../matter-detail/FeeAgreementCard.jsx";
import { InsuranceCard } from "../matter-detail/InsuranceCard.jsx";
import { ReferralCard } from "../matter-detail/ReferralCard.jsx";
import { IntakeSmsCard } from "../matter-detail/IntakeSmsCard.jsx";
import { DamagesCard } from "../matter-detail/DamagesCard.jsx";
import { DemandsCard } from "../matter-detail/DemandsCard.jsx";
import { FundingCard } from "../matter-detail/FundingCard.jsx";
// StageSopsCard retired 5-η.2 — content merged into TasksCard.
import { CrashReportCard } from "../matter-detail/CrashReportCard.jsx";
import { BankruptcyCheckCard } from "../matter-detail/BankruptcyCheckCard.jsx";
import { ProvisionFoldersCard } from "../matter-detail/ProvisionFoldersCard.jsx";
import { CaseFileCard } from "../matter-detail/CaseFileCard.jsx";
import { MatterActivityCard } from "../matter-detail/MatterActivityCard.jsx";
import { MedicalRecordsStatusCard } from "../matter-detail/MedicalRecordsStatusCard.jsx";
import { MedicalRecordsCard } from "../matter-detail/MedicalRecordsCard.jsx";
import { ChronologyCard } from "../matter-detail/ChronologyCard.jsx";
import { DeadlinesCard } from "../matter-detail/DeadlinesCard.jsx";
import { TreatmentCard } from "../matter-detail/TreatmentCard.jsx";
import { DemandContentCard } from "../matter-detail/DemandContentCard.jsx";
import { TasksCard } from "../matter-detail/TasksCard.jsx";
// SolCard intentionally NOT imported — feature is gated off per the
// firm directive (May 7), and the only usage in this file is commented
// out. Re-enable both the import and the React.createElement(SolCard...)
// line in the compliance tab when the firm wants SOL back.

export function MatterDetailPage({ matter, onBack, showNotification, setPage, authStatus }) {
  // Tab state — page is organized into 5 tabs to avoid the previous
  // 9-cards-stacked-vertically wall of UI. Default: Overview.
  const [activeTab, setActiveTab] = useState("overview");
  // Substep 5-η.5.f: shared refresh counter — TasksCard bumps this
  // when a task toggles; StageTracker useEffects on it so its progress
  // bar reflects the change immediately without a full page reload.
  const [taskChangeVersion, setTaskChangeVersion] = useState(0);
  const bumpTaskVersion = () => setTaskChangeVersion(v => v + 1);
  const [fullData, setFullData] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  // "Undo upload" — show a 60-second window after a successful generate
  // where the user can click to delete the freshly-uploaded doc from
  // Clio. Covers ~80% of the "I clicked Push on the wrong matter" case
  // per TODO. `recentGenAt` = ms timestamp of the last successful
  // generation; `undoneDocIds` tracks which docs the user has already
  // undone (so we hide their button + label them "Removed"); `nowTick`
  // is a counter we bump every second to drive the countdown re-render.
  // Same window also covers the "Undo writeback" action — when the
  // missing-fields modal pushed values back to Clio, we get an
  // undo_event_id from the backend in clio_writeback.undo_event_id.

  // Document upload & extraction state
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [currentExtraction, setCurrentExtraction] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractStep, setExtractStep] = useState("idle"); // idle | uploading | reviewing | summary | scanning
  const [generatingSummary, setGeneratingSummary] = useState(false);
  // Low-confidence review modal (<90% classification confidence)
  const [confirmUpload, setConfirmUpload] = useState(null); // {file, extraction, thumbnailUrl, docType, filename}
  const [committing, setCommitting] = useState(false);
  // Intake-form → provider review modal
  const [intakeReview, setIntakeReview] = useState(null); // { entries: [...], resolving: bool, confirming: bool }

  // Clio document scan state
  const [clioImages, setClioImages] = useState([]);
  const [showClioScanModal, setShowClioScanModal] = useState(false);
  const [scanningClio, setScanningClio] = useState(false);
  const [scanQueue, setScanQueue] = useState([]);
  const [scanIndex, setScanIndex] = useState(0);
  const [scanSelected, setScanSelected] = useState({});
  const [autoGenerateSummary, setAutoGenerateSummary] = useState(false);
  const pendingAutoGenerateRef = useRef(false);

  // Push-to-Clio review state
  const [showPushReview, setShowPushReview] = useState(false);
  const [clioCurrentData, setClioCurrentData] = useState(null);
  const [pushSelections, setPushSelections] = useState({});
  const [pushingToClio, setPushingToClio] = useState(false);

  // Provider management state
  const [showProviderModal, setShowProviderModal] = useState(false);
  // Substep 5-μ.11: reporting a bad shared contact. The paralegal who
  // just watched a fax fail is the one who knows — capturing it here,
  // where they noticed, is the difference between the directory
  // self-correcting and quietly rotting.
  // Shape: { entryId, name, currentFax } | null
  const [reportingProvider, setReportingProvider] = useState(null);
  const [reportForm, setReportForm] = useState({ report_type: "wrong_fax", proposed_value: "", evidence: "fax_failed", detail: "" });
  const [submittingReport, setSubmittingReport] = useState(false);

  const submitProviderReport = async () => {
    if (!reportingProvider) return;
    setSubmittingReport(true);
    try {
      await apiFetch(`/api/directory/${reportingProvider.entryId}/report`, {
        method: "POST",
        body: JSON.stringify({
          report_type: reportForm.report_type,
          proposed_value: reportForm.proposed_value.trim() || null,
          evidence: reportForm.evidence || null,
          detail: reportForm.detail.trim() || null,
        }),
      });
      showNotification(
        "Reported. Once confirmed, this is corrected for every firm.",
        "success");
      setReportingProvider(null);
      setReportForm({ report_type: "wrong_fax", proposed_value: "", evidence: "fax_failed", detail: "" });
    } catch (e) {
      showNotification(`Report failed: ${e.message}`, "error");
    }
    setSubmittingReport(false);
  };
  const [providerDirectory, setProviderDirectory] = useState([]);
  const [providerCategories, setProviderCategories] = useState([]);
  const [providerStates, setProviderStates] = useState([]);
  const [providerFilter, setProviderFilter] = useState("");
  const [providerCatFilter, setProviderCatFilter] = useState("");
  const [providerStateFilter, setProviderStateFilter] = useState("");
  const [matterProviders, setMatterProviders] = useState([]);
  const [addingProvider, setAddingProvider] = useState(null);
  const [refreshingProviders, setRefreshingProviders] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [showCustomProviderForm, setShowCustomProviderForm] = useState(false);
  const [customProvider, setCustomProvider] = useState({ name: "", address: "", phone: "", fax: "", email: "", category: "", state: "", save_to_directory: false });
  const [addingCustomProvider, setAddingCustomProvider] = useState(false);

  // Subrogation contact state

  // Directory batch generation state

  // Process All state
  const [processingAll, setProcessingAll] = useState(false);
  const [processAllResult, setProcessAllResult] = useState(null);

  // PDF output state

  // Workflow completion status
  const [workflowStatus, setWorkflowStatus] = useState(null);


  // Load full matter data
  useEffect(() => {
    setLoadingData(true);
    apiFetch(`/api/matters/${matter.id}`)
      .then(data => {
        setFullData(data.data || data);
        setLoadingData(false);
      })
      .catch(err => {
        console.error("Failed to load matter:", err);
        setLoadingData(false);
      });

    // Load matter providers from local DB. Cached — StageDocumentsCard
    // reads the same roster, and both used to fetch it independently.
    fetchMatterProviders(matter.id)
      .then(data => setMatterProviders(data.data || []))
      .catch(e => console.error("API call failed:", e));

    // Check workflow completion status
    apiFetch(`/api/matters/${matter.id}/workflow-status`)
      .then(data => setWorkflowStatus(data))
      .catch(e => console.error("API call failed:", e));
  }, [matter.id]);

  // Called after any action that might change workflow status (push to Clio,
  // intake summary generation, crash report push, etc.) so the UI badges
  // update without needing a page refresh.
  const refreshWorkflow = useCallback(() => {
    apiFetch(`/api/matters/${matter.id}/workflow-status`)
      .then(data => setWorkflowStatus(data))
      .catch(e => console.error("Workflow status refresh failed:", e));
  }, [matter.id]);

  // After scan queue completes, open the push review screen (using ref to avoid race)
  useEffect(() => {
    if (!pendingAutoGenerateRef.current || uploadedDocs.length === 0 || extractStep !== "idle") return;
    pendingAutoGenerateRef.current = false;
    setAutoGenerateSummary(false);
    openPushReview();
  }, [autoGenerateSummary, uploadedDocs, extractStep]);

  // Fetch current Clio data and build comparison
  const openPushReview = async () => {
    // Short-circuit: if the user only classified images without client
    // info (e.g., crash photos, intake forms only — no DL or insurance
    // card), there's nothing to push to Clio. Don't waste a click on
    // the comparison modal when every "Extracted" field would be empty.
    const extractedNow = getExtractedData();
    const hasClientInfo = Object.values(extractedNow).some(v => v && String(v).trim());
    if (!hasClientInfo) {
      const docTypes = Array.from(new Set(uploadedDocs.map(d => d.document_type).filter(Boolean)));
      const typesLabel = docTypes.length
        ? docTypes.map(t => t.replace(/_/g, " ").toLowerCase()).join(", ")
        : "documents";
      showNotification(
        `Classified ${uploadedDocs.length} ${typesLabel} — no client info to update in Clio.`,
        "success",
      );
      // Skip both the push-review modal AND the intake summary
      // generation (which would also be empty without DL/insurance data).
      return;
    }

    setShowPushReview(true);
    setClioCurrentData(null);
    try {
      const data = await apiFetch(`/api/extract/compare/${matter.id}`);
      setClioCurrentData(data);
      // Build extracted data from uploadedDocs (reuses getExtractedData)
      const extracted = getExtractedData();
      // Pre-select: use extracted value by default for all fields
      const selections = {};
      const compareFields = buildCompareFields(data, extracted);
      compareFields.forEach(f => {
        selections[f.key] = f.match ? "keep" : "extracted";
      });
      setPushSelections(selections);
    } catch (err) {
      showNotification("Could not fetch Clio data for comparison: " + err.message, "error");
    }
  };

  const buildCompareFields = (clioData, extracted) => {
    if (!clioData) return [];
    const c = clioData.contact || {};
    const addr = c.address || {};
    const fields = [];
    const add = (key, label, clioVal, extVal) => {
      const cv = (clioVal || "").toString().trim();
      const ev = (extVal || "").toString().trim();
      if (!cv && !ev) return; // skip if both empty
      fields.push({ key, label, clio: cv, extracted: ev, match: cv.toLowerCase() === ev.toLowerCase() });
    };
    add("first_name", "First Name", c.first_name, extracted.first_name);
    add("last_name", "Last Name", c.last_name, extracted.last_name);
    add("date_of_birth", "Date of Birth", c.date_of_birth, extracted.date_of_birth);
    add("street", "Street Address", addr.street, extracted.street);
    add("city", "City", addr.city, extracted.city);
    add("state", "State", addr.state, extracted.state);
    add("zip", "ZIP Code", addr.zip, extracted.zip);
    add("phone", "Phone", c.phone, extracted.phone);
    add("policy_number", "Policy Number", clioData.custom_fields?.["Policy Number"], extracted.policy_number);
    add("group_number", "Group Number", clioData.custom_fields?.["Group Number"], extracted.group_number);
    add("member_id", "Member ID", clioData.custom_fields?.["Member ID"], extracted.member_id);
    add("insurance_company", "Insurance Company", clioData.insurance_company, extracted.insurance_company);
    return fields;
  };

  const getExtractedData = () => {
    const extracted = {};
    for (const doc of uploadedDocs) {
      const raw = doc.raw_fields || {};
      if (doc.document_type === "DL_FRONT" || doc.document_type === "DL_BACK") {
        if (raw.first_name) extracted.first_name = raw.first_name;
        if (raw.last_name) extracted.last_name = raw.last_name;
        if (raw.date_of_birth) extracted.date_of_birth = raw.date_of_birth;
        if (raw.address) extracted.street = raw.address;
        if (raw.city) extracted.city = raw.city;
        if (raw.state) extracted.state = raw.state;
        if (raw.zip_code) extracted.zip = raw.zip_code;
      }
      if (doc.document_type === "INSURANCE_FRONT" || doc.document_type === "INSURANCE_BACK") {
        if (raw.policy_number) extracted.policy_number = raw.policy_number;
        if (raw.group_number) extracted.group_number = raw.group_number;
        if (raw.member_id) extracted.member_id = raw.member_id;
        if (raw.insurance_company) extracted.insurance_company = raw.insurance_company;
      }
    }
    return extracted;
  };

  const handleConfirmPush = async () => {
    setPushingToClio(true);
    const extracted = getExtractedData();
    const c = clioCurrentData?.contact || {};
    const addr = c.address || {};

    // Build update payloads based on user selections
    const contactUpdates = {};
    const customFieldUpdates = {};
    const fieldMap = { first_name: c.first_name, last_name: c.last_name, date_of_birth: c.date_of_birth, street: addr.street, city: addr.city, state: addr.state, zip: addr.zip, phone: c.phone };

    for (const [key, choice] of Object.entries(pushSelections)) {
      if (choice === "keep") continue; // keep Clio value, no update needed
      const val = extracted[key] || "";
      if (!val) continue;
      if (["policy_number", "group_number", "member_id"].includes(key)) {
        const cfName = key === "policy_number" ? "Policy Number" : key === "group_number" ? "Group Number" : "Member ID";
        customFieldUpdates[cfName] = val;
      } else if (key !== "insurance_company") {
        contactUpdates[key] = val;
      }
    }

    try {
      const res = await fetch(`${API}/api/extract/push-to-clio`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          matter_id: matter.id,
          contact_id: clioCurrentData?.contact_id,
          contact_updates: Object.keys(contactUpdates).length > 0 ? contactUpdates : null,
          custom_field_updates: Object.keys(customFieldUpdates).length > 0 ? customFieldUpdates : null,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || errData.message || `Push failed (${res.status})`);
      }
      const data = await res.json();
      if (data.success) {
        const count = (data.contact_updated?.length || 0) + (data.custom_fields_updated?.length || 0);
        showNotification(`Updated ${count} field(s) in Clio`);
      }

      setPushingToClio(false);
      setShowPushReview(false);
      refreshWorkflow();

      // Now generate the intake summary
      handleGenerateIntakeSummary();
      return;
    } catch (err) {
      showNotification("Failed to push to Clio: " + err.message, "error");
    }

    setPushingToClio(false);
  };

  const providers = fullData?.providers || [];

  // Every provider add/remove funnels through here, so this is where
  // the cached roster gets busted.
  const loadMatterProviders = () => {
    fetchMatterProviders(matter.id, { force: true })
      .then(data => setMatterProviders(data.data || []))
      .catch(e => console.error("API call failed:", e));
  };

  const openProviderModal = () => {
    apiFetch("/api/providers")
      .then(data => {
        setProviderDirectory(data.data || []);
        setProviderCategories(data.categories || []);
        setProviderStates(data.states || []);
        setShowProviderModal(true);
      })
      .catch(e => console.error("API call failed:", e));
    // Also fetch refresh status
    apiFetch("/api/providers/refresh-status")
      .then(data => setLastRefreshedAt(data.last_refreshed_at || null))
      .catch(() => {});
  };


  const handleRefreshProviders = () => {
    setRefreshingProviders(true);
    fetch("/api/providers/refresh", { method: "POST" })
      .then(r => {
        if (!r.ok) throw new Error("Refresh request failed");
        return r.json();
      })
      .then(data => {
        if (data.success) {
          setLastRefreshedAt(data.last_refreshed_at || null);
          // Build diff message
          const diff = data.diff;
          if (diff) {
            const changes = Object.values(diff).reduce((sum, d) => sum + (d.added || 0) + (d.removed || 0), 0);
            if (changes > 0) {
              showNotification && showNotification("Provider directory refreshed (" + changes + " change(s) detected)", "success");
            } else {
              showNotification && showNotification("Provider directory refreshed — no changes", "success");
            }
          } else {
            showNotification && showNotification("Provider directory refreshed", "success");
          }
          // Re-fetch the provider list
          return apiFetch("/api/providers");
        }
        throw new Error("Refresh returned unsuccessful");
      })
      .then(data => {
        if (data) {
          setProviderDirectory(data.data || []);
          setProviderCategories(data.categories || []);
          setProviderStates(data.states || []);
        }
      })
      .catch(err => showNotification && showNotification("Refresh failed: " + err.message, "error"))
      .finally(() => setRefreshingProviders(false));
  };

  const addProviderToMatter = async (providerId) => {
    setAddingProvider(providerId);
    try {
      const res = await fetch("/api/matters/" + matter.id + "/providers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider_id: providerId }),
      });
      if (res.ok) {
        loadMatterProviders();
        apiFetch(`/api/matters/${matter.id}`)
          .then(data => setFullData(data.data || data))
          .catch(e => console.error("API call failed:", e));
        showNotification && showNotification("Provider added", "success");
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification && showNotification("Failed to add provider: " + (err.detail || err.error || "unknown error"), "error");
      }
    } catch (e) {
      console.error(e);
      showNotification && showNotification("Failed to add provider: " + e.message, "error");
    }
    setAddingProvider(null);
  };

  const addCustomProviderToMatter = async () => {
    if (!customProvider.name.trim()) return;
    setAddingCustomProvider(true);
    try {
      const { save_to_directory, ...matterPayload } = customProvider;
      const res = await fetch(`/api/matters/${matter.id}/providers/custom`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(matterPayload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showNotification("Failed: " + (err.detail || err.error || "unknown error"), "error");
        setAddingCustomProvider(false);
        return;
      }

      let dirMessage = "";
      if (save_to_directory) {
        try {
          const dirRes = await fetch("/api/providers/directory/medical", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: customProvider.name,
              address: customProvider.address,
              phone: customProvider.phone,
              fax: customProvider.fax,
              email: customProvider.email,
              category: customProvider.category,
              state: customProvider.state,
            }),
          });
          if (dirRes.ok) {
            dirMessage = " and saved to directory";
          } else {
            const err = await dirRes.json().catch(() => ({}));
            dirMessage = " (directory save failed: " + (err.detail || "unknown") + ")";
          }
        } catch (e) {
          dirMessage = " (directory save failed: " + e.message + ")";
        }
      }

      loadMatterProviders();
      apiFetch(`/api/matters/${matter.id}`)
        .then(data => setFullData(data.data || data))
        .catch(e => console.error("API call failed:", e));
      showNotification("Custom provider added" + dirMessage, dirMessage.includes("failed") ? "warning" : "success");
      setCustomProvider({ name: "", address: "", phone: "", fax: "", email: "", category: "", state: "", save_to_directory: false });
      setShowCustomProviderForm(false);
    } catch (e) {
      showNotification("Failed: " + e.message, "error");
    }
    setAddingCustomProvider(false);
  };

  const removeProvider = async (recordId) => {
    if (!window.confirm("Remove this provider from the matter?")) return;
    try {
      const res = await fetch(`/api/matters/${matter.id}/providers/${recordId}`, { method: "DELETE" });
      if (res.ok) {
        loadMatterProviders();
        apiFetch(`/api/matters/${matter.id}`)
          .then(data => setFullData(data.data || data))
          .catch(e => console.error("API call failed:", e));
        showNotification && showNotification("Provider removed", "success");
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification && showNotification("Failed to remove provider: " + (err.detail || err.error || "unknown error"), "error");
      }
    } catch (e) {
      console.error(e);
      showNotification && showNotification("Failed to remove provider: " + e.message, "error");
    }
  };

  // --- Document Upload & Extraction handlers ---
  const LOW_CONFIDENCE_THRESHOLD = 90;

  const _processClassificationResponse = (file, data) => {
    if (data.pending_confirmation) {
      // Low-confidence classification — open review modal so the user can
      // confirm/override doc type + filename before anything lands in Clio.
      setConfirmUpload({
        file,
        extraction: data,
        thumbnailUrl: URL.createObjectURL(file),
        docType: data.document_type || "UNKNOWN",
        filename: data.suggested_filename || file.name || "upload.jpg",
      });
      setExtractStep("idle");
      return;
    }
    setCurrentExtraction(data);
    setExtractStep("reviewing");
  };

  // One upload path for both entry points. The drop target used to
  // inline its own copy of this fetch with slightly different error
  // handling — a dropped file that failed got a vaguer message than a
  // picked one.
  const startClassifyUpload = async (file) => {
    if (!file) return;
    setExtractStep("uploading");
    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const url = `${API}/api/extract/classify?matter_id=${matter.id}&min_confidence_to_upload=${LOW_CONFIDENCE_THRESHOLD}`;
      const res = await fetch(url, { method: "POST", body: formData });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(errData.detail || `Server error (${res.status})`);
      }
      const data = await res.json();

      if (data.success) {
        _processClassificationResponse(file, data);
      } else {
        throw new Error(data.error || "Classification failed");
      }
    } catch (err) {
      showNotification(err.message, "error");
      setExtractStep("idle");
    }
    setExtracting(false);
  };

  const handleDocUpload = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => startClassifyUpload(e.target.files[0]);
    input.click();
  };

  const DOC_TYPE_OPTIONS = [
    { value: "DL_FRONT", label: "Driver's License — Front" },
    { value: "DL_BACK", label: "Driver's License — Back" },
    { value: "INSURANCE_FRONT", label: "Insurance Card — Front" },
    { value: "INSURANCE_BACK", label: "Insurance Card — Back" },
    { value: "CRASH_PHOTO", label: "Picture of Wreck" },
    { value: "INTAKE_FORM", label: "Intake Form" },
    { value: "UNKNOWN", label: "Other / Unknown" },
  ];

  const cancelConfirmUpload = () => {
    if (confirmUpload?.thumbnailUrl) URL.revokeObjectURL(confirmUpload.thumbnailUrl);
    setConfirmUpload(null);
  };

  const submitConfirmUpload = async () => {
    if (!confirmUpload) return;
    const { file, docType, filename, extraction } = confirmUpload;
    if (!filename.trim()) {
      showNotification("Please enter a filename.", "warning");
      return;
    }
    setCommitting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const qs = new URLSearchParams({
        matter_id: String(matter.id),
        doc_type: docType,
        filename: filename.trim(),
      });
      const res = await fetch(`${API}/api/extract/commit?${qs}`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Server error (${res.status})`);
      }
      const commitData = await res.json();

      // Merge the commit result into the extraction so the normal review
      // flow can continue (field review, intake summary, etc.)
      const merged = {
        ...extraction,
        document_type: docType,
        clio_upload: commitData.clio_upload,
        suggested_filename: commitData.clio_upload?.filename || filename.trim(),
        pending_confirmation: false,
      };
      if (confirmUpload.thumbnailUrl) URL.revokeObjectURL(confirmUpload.thumbnailUrl);
      setConfirmUpload(null);
      setCurrentExtraction(merged);
      setExtractStep("reviewing");
      showNotification(`Uploaded as ${commitData.clio_upload?.filename || filename}`, "success");
    } catch (err) {
      showNotification("Upload failed: " + err.message, "error");
    }
    setCommitting(false);
  };

  const handleAcceptExtraction = () => {
    if (currentExtraction) {
      // Replace existing doc of same type, or add new
      setUploadedDocs(prev => {
        const filtered = prev.filter(d => d.document_type !== currentExtraction.document_type);
        return [...filtered, currentExtraction];
      });
    }
    setCurrentExtraction(null);
    setExtractStep("idle");
  };

  const handleDiscardExtraction = () => {
    setCurrentExtraction(null);
    setExtractStep("idle");
  };

  // --- Intake form → medical provider resolution ---
  const parseIntakeProviders = (extraction) => {
    const raw = extraction?.raw_fields || {};
    // Preferred: structured list [{name, location}]
    let detailed = raw.treatment_providers_detailed;
    if (typeof detailed === "string") {
      try { detailed = JSON.parse(detailed); } catch { detailed = null; }
    }
    if (Array.isArray(detailed) && detailed.length > 0) {
      return detailed
        .map(p => ({ name: (p.name || "").trim(), location: (p.location || "").trim() }))
        .filter(p => p.name);
    }
    // Legacy: comma-separated names
    const flat = (raw.treatment_providers || "").split(",").map(s => s.trim()).filter(Boolean);
    return flat.map(n => ({ name: n, location: "" }));
  };

  const openIntakeProviderReview = async (extraction) => {
    const providers = parseIntakeProviders(extraction);
    if (providers.length === 0) {
      showNotification("No medical providers found in this intake form.", "warning");
      return;
    }
    setIntakeReview({ entries: [], resolving: true, confirming: false });
    try {
      const data = await apiFetch(`/api/matters/${matter.id}/intake-providers/resolve`, {
        method: "POST",
        body: JSON.stringify({ providers }),
      });
      const entries = (data.resolved || []).map(r => ({
        ...r,
        include: r.source === "directory" ? true : false, // new providers default off so attorney reviews
        save_to_directory: r.source === "web_search",     // default ON for new ones; attorney can uncheck
      }));
      setIntakeReview({ entries, resolving: false, confirming: false });
    } catch (err) {
      setIntakeReview(null);
      showNotification("Could not resolve providers: " + err.message, "error");
    }
  };

  const updateIntakeEntry = (idx, patch) => {
    setIntakeReview(prev => {
      if (!prev) return prev;
      const entries = prev.entries.map((e, i) => i === idx ? { ...e, ...patch } : e);
      return { ...prev, entries };
    });
  };

  // Scan the intake form in folder 1 (or matter root) and open the
  // intake-providers review modal with the extracted + resolved entries.
  // Triggered by the "Scan intake form" button on the Providers tab.
  const [scanningIntake, setScanningIntake] = useState(false);
  const scanIntakeFormForProviders = async () => {
    if (scanningIntake) return;
    setScanningIntake(true);
    setIntakeReview({ entries: [], resolving: true, confirming: false });
    try {
      const data = await apiFetch(
        `/api/matters/${matter.id}/intake-providers/scan-from-form`,
        { method: "POST" },
      );
      const resolved = data.resolved || [];
      if (resolved.length === 0) {
        setIntakeReview(null);
        showNotification(data.message || "No providers found.", "warning");
        return;
      }
      const entries = resolved.map(r => ({
        ...r,
        include: r.source === "directory" ? true : false,
        add_to_clio: true,
        save_to_directory: r.source === "web_search",
      }));
      setIntakeReview({
        entries,
        resolving: false,
        confirming: false,
        intakeFormName: data.intake_form?.name,
      });
    } catch (err) {
      setIntakeReview(null);
      showNotification("Could not scan intake form: " + err.message, "error");
    } finally {
      // Critical — must run on every code path. Previously the empty-result
      // `return` above bypassed this, leaving the button stuck on "Scanning..."
      setScanningIntake(false);
    }
  };

  const confirmIntakeProviders = async () => {
    if (!intakeReview) return;
    const approved = intakeReview.entries.filter(e => e.include && (e.name || "").trim());
    if (approved.length === 0) {
      showNotification("Check at least one provider before confirming.", "warning");
      return;
    }
    setIntakeReview(prev => prev && ({ ...prev, confirming: true }));
    try {
      const payload = {
        providers: approved.map(e => ({
          name: e.name,
          address: e.address || "",
          phone: e.phone || "",
          fax: e.fax || "",
          email: e.email || "",
          category: e.category || "",
          city: e.city || "",
          state: e.state || "",
          directory_provider_id: e.directory_provider_id || null,
          add_to_clio: e.add_to_clio !== false,            // default true if undefined
          save_to_directory: !!e.save_to_directory && !e.directory_provider_id,
        })),
      };
      const data = await apiFetch(`/api/matters/${matter.id}/intake-providers/confirm`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const addedCount = data.added_count || 0;
      const sheetCount = (data.results || []).filter(r => r.sheet_added).length;
      const pushErrors = (data.results || []).filter(r => r.clio_push_error).length;
      let msg = `Added ${addedCount} provider${addedCount === 1 ? "" : "s"} to the matter`;
      if (sheetCount) msg += ` (${sheetCount} saved to directory)`;
      if (pushErrors) msg += ` — ${pushErrors} Clio push warning${pushErrors === 1 ? "" : "s"}`;
      showNotification(msg, pushErrors ? "warning" : "success");
      setIntakeReview(null);
      loadMatterProviders();
    } catch (err) {
      showNotification("Failed to add providers: " + err.message, "error");
      setIntakeReview(prev => prev && ({ ...prev, confirming: false }));
    }
  };

  const handleGenerateIntakeSummary = async () => {
    setGeneratingSummary(true);
    setExtractStep("summary");
    try {
      const res = await fetch(`${API}/api/extract/intake-summary`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          matter_id: matter.id,
          extractions: uploadedDocs.map(d => ({
            document_type: d.document_type,
            raw_fields: d.raw_fields,
          })),
          client_name: fullData?.client_name || matter.client?.name || undefined,
        }),
      });
      const data = await res.json();
      if (data.success) {
        if (data.name_mismatch) {
          showNotification(
            `Intake summary uploaded. Note: ${data.name_mismatch.message}`,
            "warning"
          );
        } else {
          showNotification(`Intake summary uploaded to Clio (${data.filename})`);
        }
        refreshWorkflow();
      } else {
        throw new Error(data.detail || "Failed to generate intake summary");
      }
    } catch (err) {
      showNotification(err.message, "error");
    }
    setGeneratingSummary(false);
    setExtractStep("idle");
  };

  // --- Clio Document Scan handlers ---
  const handleOpenClioScan = async () => {
    setScanningClio(true);
    setShowClioScanModal(true);
    setScanSelected({});
    try {
      const data = await apiFetch(`/api/matters/${matter.id}/documents`);
      setClioImages(data.data || []);
    } catch (err) {
      showNotification(err.message, "error");
      setClioImages([]);
    }
    setScanningClio(false);
  };

  const toggleScanSelect = (docId) => {
    setScanSelected(prev => {
      const next = { ...prev };
      if (next[docId]) delete next[docId];
      else next[docId] = true;
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (Object.keys(scanSelected).length === clioImages.length) {
      setScanSelected({});
    } else {
      const all = {};
      clioImages.forEach(d => { all[d.id] = true; });
      setScanSelected(all);
    }
  };

  const handleStartScanQueue = () => {
    const selected = clioImages.filter(d => scanSelected[d.id]);
    if (selected.length === 0) return;
    setScanQueue(selected);
    setScanIndex(0);
    setShowClioScanModal(false);
    setExtractStep("uploading");
    setExtracting(true);
    processClioDoc(selected, 0);
  };

  // Per-image failures collected as the queue runs. Surfaced as a
  // single summary notification at queue completion so the user can
  // see what was skipped without each toast feeling like a fatal error.
  const scanFailuresRef = useRef([]);

  const processClioDoc = async (queue, idx) => {
    setExtractStep("uploading");
    setExtracting(true);
    const doc = queue[idx];
    try {
      const res = await fetch(`${API}/api/extract/classify-clio-doc`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ document_id: doc.id, matter_id: matter.id }),
      });
      let data;
      try {
        data = await res.json();
      } catch {
        // Server returned a non-JSON response (likely a 500 HTML page).
        // Treat as a structured failure; queue continues.
        data = { success: false, error: `HTTP ${res.status}: ${res.statusText || "non-JSON response"}` };
      }
      if (data.success) {
        setCurrentExtraction(data);
        setExtractStep("reviewing");
      } else {
        // Skip this doc — surface a brief inline toast for visibility,
        // record the failure for the end-of-queue summary, and advance.
        const reason = data.error || "Classification failed";
        // Show enough of the error to actually diagnose. The full reason
        // is logged to console + collected for the end-of-queue summary.
        const shortReason = reason.length > 280 ? reason.slice(0, 280) + "…" : reason;
        console.warn(`[scan-clio] full error for ${doc.name}:`, reason);
        showNotification(
          `Skipped "${doc.name}": ${shortReason}`,
          "warning",
        );
        scanFailuresRef.current.push({ name: doc.name, reason });
        advanceScanQueue(queue, idx);
      }
    } catch (err) {
      const reason = err.message || "Unknown error";
      const shortReason = reason.length > 280 ? reason.slice(0, 280) + "…" : reason;
      console.warn(`[scan-clio] full error for ${doc.name}:`, reason);
      showNotification(
        `Skipped "${doc.name}": ${shortReason}`,
        "warning",
      );
      scanFailuresRef.current.push({ name: doc.name, reason });
      advanceScanQueue(queue, idx);
    }
    setExtracting(false);
  };

  const advanceScanQueue = (queue, idx, hasAccepted) => {
    const nextIdx = idx + 1;
    if (nextIdx < queue.length) {
      setScanIndex(nextIdx);
      processClioDoc(queue, nextIdx);
    } else {
      // Queue finished — surface the end-of-queue summary so the user
      // knows it completed AND sees how many failed at a glance.
      const failures = scanFailuresRef.current;
      const total = queue.length;
      const failed = failures.length;
      const succeeded = total - failed;
      if (failed > 0) {
        const sample = failures.slice(0, 3).map(f => `"${f.name}"`).join(", ");
        const moreNote = failures.length > 3 ? ` (+${failures.length - 3} more)` : "";
        showNotification(
          `Scan complete: ${succeeded} of ${total} classified. ${failed} skipped: ${sample}${moreNote}`,
          succeeded > 0 ? "warning" : "error",
        );
      } else {
        showNotification(
          `Scan complete: ${total} of ${total} classified.`,
          "success",
        );
      }
      // Reset for next run
      scanFailuresRef.current = [];
      setScanQueue([]);
      setScanIndex(0);
      setExtractStep("idle");
      if (hasAccepted) {
        // Trigger auto-generation via useEffect (uses ref to avoid race)
        pendingAutoGenerateRef.current = true;
        setAutoGenerateSummary(true);
      }
    }
  };

  const handleAcceptScanExtraction = () => {
    if (currentExtraction) {
      setUploadedDocs(prev => {
        const filtered = prev.filter(d => d.document_type !== currentExtraction.document_type);
        return [...filtered, currentExtraction];
      });
    }
    setCurrentExtraction(null);
    if (scanQueue.length > 0) {
      advanceScanQueue(scanQueue, scanIndex, true);
    } else {
      setExtractStep("idle");
    }
  };

  const handleDiscardScanExtraction = () => {
    setCurrentExtraction(null);
    if (scanQueue.length > 0) {
      // Check if we have any accepted docs so far
      advanceScanQueue(scanQueue, scanIndex, uploadedDocs.length > 0);
    } else {
      setExtractStep("idle");
    }
  };

  // --- Process All Images handler ---
  const handleProcessAll = async () => {
    setProcessingAll(true);
    setProcessAllResult(null);
    try {
      const data = await apiFetch(`/api/extract/process-all/${matter.id}`, { method: "POST" });
      setProcessAllResult(data);
      if (data.processed?.length > 0) {
        // Store extractions for potential push-to-clio
        const newDocs = (data.extractions || []).filter(e => e.success);
        if (newDocs.length > 0) {
          setUploadedDocs(prev => {
            let updated = [...prev];
            for (const ext of newDocs) {
              updated = updated.filter(d => d.document_type !== ext.document_type);
              updated.push(ext);
            }
            return updated;
          });
        }
        const msg = `Processed ${data.processed.length} document(s)` +
          (data.intake_summary ? " and generated intake summary" : "");
        showNotification(msg, "success");
      } else {
        showNotification(data.message || "No unprocessed images found", "warning");
      }
      if (data.warnings?.length) {
        data.warnings.forEach(w => showNotification(w, "warning"));
      }
      refreshWorkflow();
    } catch (err) {
      showNotification("Process all failed: " + err.message, "error");
    }
    setProcessingAll(false);
  };

  const getConfidenceColor = (conf) => {
    if (conf >= 95) return C.success;
    if (conf >= 85) return C.gold;
    return C.error;
  };

  const clientName = fullData?.client_name || matter.client?.name || "Loading...";
  const displayNum = fullData?.matter_number || matter.display_number || "";

  return React.createElement("div", { className:"fade-in", style: { padding:"32px 40px", maxWidth:"1100px" } },

    // Back
    React.createElement("button", {
      onClick: onBack,
      style: { display:"flex", alignItems:"center", gap:"6px", border:"none", background:"none", cursor:"pointer", fontSize:"13px", color:C.textMuted, fontWeight:500, marginBottom:"20px", padding:0 }
    }, I.back, "Back to Matters"),

    // Matter Header Card
    React.createElement("div", {
      style: { background:"#fff", borderRadius:"12px", padding:"24px 28px", border:`1px solid ${C.warmGray}`, marginBottom:"20px" }
    },
      React.createElement("div", { style: { display:"flex", justifyContent:"space-between", alignItems:"flex-start" } },
        React.createElement("div", null,
          React.createElement("h1", {
            style: { fontFamily:"'Playfair Display',serif", fontSize:"24px", fontWeight:600, color:C.navy, marginBottom:"4px" }
          }, clientName),
          React.createElement("div", { style: { fontSize:"14px", color:C.textMuted } }, displayNum),
        ),
        React.createElement(Badge, { variant:"gold" }, matter.status || "Open"),
      ),

      loadingData
        ? React.createElement("div", { style: { padding:"20px", textAlign:"center" } }, React.createElement(Spinner, null))
        : fullData && React.createElement("div", {
            style: { display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:"16px", marginTop:"20px", paddingTop:"20px", borderTop:`1px solid ${C.warmGray}60` }
          },
            [
              { label: "Attorney", value: fullData.attorney_name || "\u2014" },
              { label: "Date of Incident", value: fullData.date_of_incident || "\u2014" },
              { label: "Insurance", value: fullData.insurance_company || "\u2014" },
              { label: "Adjuster", value: fullData.adjuster_name || "\u2014" },
              { label: "SSN", value: fullData.ssn_masked || "\u2014" },
              { label: "Providers", value: `${providers.length} on file` },
            ].map((item, i) =>
              React.createElement("div", { key: i },
                React.createElement("div", { style: { fontSize:"11px", color:C.textLight, textTransform:"uppercase", letterSpacing:"0.06em", fontWeight:600, marginBottom:"4px" } }, item.label),
                React.createElement("div", { style: { fontSize:"14px", color:C.navy, fontWeight:500 } }, item.value),
              )
            ),
          ),
    ),

    // ============ Tab Navigation ============
    // `tab-bar` makes this scroll sideways on a phone instead of
    // overflowing the page. Five tabs need roughly 450px; a phone has
    // 375px. Wrapping to two lines costs vertical space on the screen
    // that has least of it, and a dropdown hides where you are — a
    // scrolling strip is what iOS itself does. See responsive.css.
    React.createElement("div", {
      className: "tab-bar",
      style: {
        display:"flex", gap:"4px", marginBottom:"20px",
        borderBottom:`1px solid ${C.warmGray}`,
      }
    },
      [
        { id: "overview", label: "Overview" },
        { id: "documents", label: "Documents" },
        { id: "compliance", label: "Compliance" },
        { id: "providers", label: "Providers" },
        { id: "activity", label: "Activity" },
      ].map(tab => {
        const isActive = activeTab === tab.id;
        return React.createElement("button", {
          key: tab.id,
          onClick: () => setActiveTab(tab.id),
          style: {
            padding:"10px 18px",
            border:"none",
            background:"transparent",
            borderBottom: isActive ? `2px solid ${C.gold}` : "2px solid transparent",
            color: isActive ? C.navy : C.textMuted,
            fontSize:"13px",
            fontWeight: isActive ? 700 : 500,
            cursor:"pointer",
            marginBottom:"-1px",
            transition:"color 0.15s, border-color 0.15s",
          },
        }, tab.label);
      })
    ),

    // ============ Tab: Overview ============
    // Substep 5-η.5.d: Case Stage + Workflow Tasks share one card so
    // the progress bar and the tasks that produce it visually belong
    // together. Both rendered with `bare` = drop their own outer card;
    // wrapper below provides a single card + divider between them.
    React.createElement(TabPanel, { activeTab, ids: "overview" },
      React.createElement("div", {
        style: {
          background: "#fff", borderRadius: "12px",
          border: `1px solid ${C.warmGray}`, marginBottom: "20px",
          overflow: "hidden",
        },
      },
        React.createElement(StageTracker, {
          matterId: matter.id, showNotification, bare: true,
          refreshTrigger: taskChangeVersion,
        }),
        React.createElement("div", {
          style: { height: "1px", background: C.warmGray, margin: "0 24px" },
        }),
        React.createElement(TasksCard, {
          matterId: matter.id, showNotification, authStatus, bare: true,
          onTaskChange: bumpTaskVersion,
        }),
      ),
    ),

    // Coverage on Overview rather than Compliance: policy limits are the
    // ceiling on what the case can be worth, so they shape how the file
    // is worked from day one. Compliance is settlement-end work.
    // Where the case came from, on Overview. It is intake information,
    // it takes seconds, and the Referral sources report is only as good
    // as how often somebody fills it in — burying it would guarantee an
    // empty report.
    // Intake sits on Overview because that is where somebody opening a
    // new matter is already looking, and chasing the form is the first
    // thing that happens on a file.
    React.createElement(TabPanel, { activeTab, ids: "overview" },
      React.createElement(IntakeSmsCard, { matterId: matter.id, showNotification }),
    ),

    React.createElement(TabPanel, { activeTab, ids: "overview" },
      React.createElement(ReferralCard, { matterId: matter.id, showNotification }),
    ),

    React.createElement(TabPanel, { activeTab, ids: "overview" },
      React.createElement(InsuranceCard, { matterId: matter.id, showNotification }),
    ),

    // Damages under coverage, by the same argument: limits are the
    // ceiling on what the case can be worth and this is the floor being
    // built toward it. Both are day-one work — wage loss accrues during
    // treatment, and a client asked about it months later remembers it
    // wrong.
    React.createElement(TabPanel, { activeTab, ids: "overview" },
      React.createElement(DamagesCard, { matterId: matter.id, showNotification }),
    ),

    // Deadlines lead the Overview tab in importance even though they
    // render below: a blown statute ends the case, and this is where
    // the dates are computed from the matter's own state of loss.
    React.createElement(TabPanel, { activeTab, ids: "overview" },
      React.createElement(DeadlinesCard, { matterId: matter.id, showNotification }),
    ),

    // Substep 5-π.2: the Overview copy of StageDocumentsCard is gone.
    // It rendered the same "Documents for this stage" template list the
    // Documents tab renders, so the same section appeared on two tabs —
    // and being a second mounted instance it re-fetched the stage
    // dashboard, templates, drafts, providers and dispatches on every
    // matter open. Document generation lives on the Documents tab.

    // ============ Tab: Compliance ============
    // SOL card temporarily hidden per firm directive (May 7) — the
    // SolCard component, /api/matters/{id}/sol endpoints, sol_service,
    // and the inline state-recovery flow all remain intact in the code
    // base. Re-enable by uncommenting the line below when the firm is
    // ready to use this feature.
    // activeTab === "compliance" && React.createElement(SolCard, { matterId: matter.id, showNotification }),

    React.createElement(TabPanel, { activeTab, ids: "compliance" },
      React.createElement(BankruptcyCheckCard, { matterId: matter.id, showNotification }),
    ),

    // Liens sit with compliance rather than documents: the work is
    // verifying and reducing claims against the settlement, and the
    // disbursement cannot execute while one is open.
    React.createElement(TabPanel, { activeTab, ids: "compliance" },
      React.createElement(LiensCard, { matterId: matter.id, showNotification }),
    ),

    // Costs sit under liens: both are claims against the same pot, and
    // both feed the disbursement above.
    React.createElement(TabPanel, { activeTab, ids: "compliance" },
      React.createElement(CaseCostsCard, { matterId: matter.id, showNotification }),
    ),

    // Last on the tab because it consumes the two above it — the
    // disbursement is only meaningful once liens and costs are known.
    // Above the settlement card because it decides that card's
    // arithmetic — and because it is where the fee-tier warning tells
    // people to go. Until this existed there was nowhere to go.
    React.createElement(TabPanel, { activeTab, ids: "compliance" },
      React.createElement(FeeAgreementCard, { matterId: matter.id, showNotification }),
    ),

    React.createElement(TabPanel, { activeTab, ids: "compliance" },
      React.createElement(SettlementCard, { matterId: matter.id, showNotification }),
    ),

    // ============ Tab: Documents ============
    // The file cabinet leads, because "Documents" is what it means in
    // Clio — the matter's actual files. Everything below it on this tab
    // is about MAKING documents, which is the second question somebody
    // opening this tab has, not the first.
    React.createElement(TabPanel, { activeTab, ids: "documents" },
      React.createElement(CaseFileCard, { matterId: matter.id, showNotification }),
    ),

    // Substep 5-π.2: one card, not two. Generate Package used to be its
    // own card directly above this one, and both opened by announcing
    // the current stage and offering that stage's templates — bulk vs.
    // one at a time. Now the bulk button is the header action on the
    // card that already lists what it would generate, with the drafts
    // it produces in the same card below.
    React.createElement(TabPanel, { activeTab, ids: "documents" },
      React.createElement(StageDocumentsCard, {
        matterId: matter.id, showNotification, showDrafts: true,
        headerAction: React.createElement(StagePackageCard, {
          matterId: matter.id, showNotification, headerOnly: true,
        }),
        // Substep 5-π.5: the ad-hoc path used to be its own dashed card
        // below this one asking "Need something this stage doesn't
        // list?" — a whole card to hold one question. It is a link at
        // the foot of the template list now, where someone who just
        // failed to find their template is already looking.
        onOpenAdHoc: setPage ? () => setPage("generate") : null,
      }),
    ),

    // Substep 5-ο.3: composed demand content lives on the Documents
    // tab because that's where generation happens — but it's sourced
    // from the medical records on the Providers tab. Loads only on
    // explicit click; composing the chronology is an LLM call.
    React.createElement(TabPanel, { activeTab, ids: "documents" },
      React.createElement(DemandContentCard, { matterId: matter.id, showNotification }),
    ),

    // Demand tracking follows demand content — the order the work
    // happens in: compose the chronology, generate the letter, send it,
    // then live with the clock.
    React.createElement(TabPanel, { activeTab, ids: "documents" },
      React.createElement(DemandsCard, { matterId: matter.id, showNotification }),
    ),

    React.createElement(TabPanel, { activeTab, ids: "documents" },
      React.createElement(ProvisionFoldersCard, { matterId: matter.id, showNotification }),
    ),

    React.createElement(TabPanel, { activeTab, ids: "documents" },
      React.createElement(CrashReportCard, {
        matterId: matter.id,
        clientName: fullData?.client_name || matter.client?.name || "",
        showNotification,
        previouslyPushed: workflowStatus?.crash_report_pushed,
        previousPushDetail: workflowStatus?.crash_report_detail,
        onWorkflowUpdate: refreshWorkflow,
      }),
    ),

    // ============ Tab: Providers ============
    // Records Status card sits at the top of the Providers tab — gives
    // a quick "what are we waiting on" view before the static provider
    // roster. Computes on demand (no polling) since records arrive at
    // human speed (days), not bot speed.
    React.createElement(TabPanel, { activeTab, ids: "providers" },
      React.createElement(MedicalRecordsStatusCard, {
        matterId: matter.id, showNotification,
      }),
    ),

    // Substep 5-ξ.3: records extraction + treatment rollup. Sits under
    // the records-status card — that one tracks what's been REQUESTED,
    // this one processes what came BACK.
    React.createElement(TabPanel, { activeTab, ids: "providers" },
      React.createElement(MedicalRecordsCard, {
        matterId: matter.id, showNotification,
      }),
    ),

    // Outline 3.16. Sits directly under the records it is built from,
    // rather than over on Documents with the demand that consumes it:
    // editing a paragraph means checking it against the encounter, and
    // that is the tab holding the encounter.
    React.createElement(TabPanel, { activeTab, ids: "providers" },
      React.createElement(ChronologyCard, { matterId: matter.id, showNotification }),
    ),

    // Injuries and care gaps sit with the records they are derived
    // from, above the chronology that narrates them.
    React.createElement(TabPanel, { activeTab, ids: "providers" },
      React.createElement(TreatmentCard, { matterId: matter.id, showNotification }),
    ),

    // Funding sits with the treatment it pays for: who is covering
    // these visits, and what happens when that runs out.
    React.createElement(TabPanel, { activeTab, ids: "providers" },
      React.createElement(FundingCard, { matterId: matter.id, showNotification }),
    ),
    React.createElement(TabPanel, { activeTab, ids: "providers" },
    React.createElement("div", {
      style: { background:"#fff", borderRadius:"12px", padding:"24px 28px", border:`1px solid ${C.warmGray}`, marginBottom:"20px" }
    },
      React.createElement("div", { style: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px", gap:"8px", flexWrap:"wrap" } },
        React.createElement("h2", {
          style: { fontSize:"13px", color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:600 }
        }, "Medical Providers"),
        React.createElement("div", { style: { display:"flex", gap:"8px", flexWrap:"wrap" } },
          React.createElement("button", {
            onClick: scanIntakeFormForProviders,
            disabled: scanningIntake,
            title: "Find the client's intake form in folder 1 or matter root, extract listed providers, match against directory, web-search for unknowns",
            style: {
              padding:"6px 14px", borderRadius:"8px", border:"none",
              background: scanningIntake ? C.warmGray : C.gold, color:"#fff",
              fontSize:"12px", fontWeight:600,
              cursor: scanningIntake ? "wait" : "pointer",
              display:"flex", alignItems:"center", gap:"4px",
            }
          }, scanningIntake ? "Scanning..." : "Scan intake form"),
          React.createElement("button", {
            onClick: openProviderModal,
            style: {
              padding:"6px 14px", borderRadius:"8px", border:`1px solid ${C.navy}`,
              background:"transparent", color:C.navy, fontSize:"12px", fontWeight:600, cursor:"pointer",
              display:"flex", alignItems:"center", gap:"4px",
            }
          }, I.plus, " Add Provider"),
        ),
      ),
      matterProviders.length === 0 && providers.length === 0
        ? React.createElement("div", { style: { padding:"20px", textAlign:"center", color:C.textLight, fontSize:"13px" } },
            "No providers assigned. Add providers to enable medical document generation."
          )
        : React.createElement("div", { style: { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:"12px" } },
            (matterProviders.length > 0 ? matterProviders : providers.map((p, i) => ({
              id: i, name: p.medical_provider?.name || "Unknown", category: p.description || "",
              address: p._address || "", phone: p._phone || "", fax: p._fax || "",
            }))).map(p =>
              React.createElement("div", {
                key: p.id,
                style: {
                  padding:"14px 16px", borderRadius:"8px", border:`1px solid ${C.warmGray}`,
                  background:C.warmWhite, display:"flex", justifyContent:"space-between", alignItems:"flex-start",
                }
              },
                React.createElement("div", null,
                  React.createElement("div", { style: { fontSize:"14px", fontWeight:600, color:C.navy } }, p.name),
                  React.createElement("div", { style: { fontSize:"11px", color:C.gold, fontWeight:500, marginTop:"2px" } }, p.category),
                  p.address && React.createElement("div", { style: { fontSize:"12px", color:C.textMuted, marginTop:"4px" } }, p.address),
                  p.phone && React.createElement("div", { style: { fontSize:"12px", color:C.textMuted } }, p.phone),
                  p.fax && React.createElement("div", { style: { fontSize:"12px", color:C.textMuted } }, "Fax: ", p.fax),
                ),
                matterProviders.length > 0 && React.createElement("button", {
                  onClick: () => removeProvider(p.id),
                  title: "Remove provider",
                  style: {
                    border:"none", background:"none", cursor:"pointer", color:C.textLight, fontSize:"16px",
                    padding:"2px 6px", borderRadius:"4px", flexShrink:0,
                  }
                }, "\u00d7"),
              )
            ),
          ),
    )),

    // ============ Tab: Activity (full audit log) ============
    // Substep 5-η.3: dropped the Overview preview — full card lives on
    // the Activity tab; showing it twice bloated Overview without adding
    // information paralegals didn't already have one tab away.
    React.createElement(TabPanel, { activeTab, ids: "activity" },
      React.createElement(MatterActivityCard, { matterId: matter.id, setPage }),
    ),

    // Substep 5-μ.11: report-a-bad-contact modal. Sits above the
    // directory modal so it's usable without losing the picker.
    reportingProvider && React.createElement("div", {
      role: "dialog", "aria-modal": "true", "aria-label": "Report contact problem",
      style: {
        position:"fixed", inset:0, background:"rgba(27,42,74,0.6)",
        display:"flex", alignItems:"center", justifyContent:"center", zIndex:1100, padding:"40px 20px",
      },
      onClick: e => { if (e.target === e.currentTarget) setReportingProvider(null); },
    },
      React.createElement("div", {
        style: { background:"#fff", borderRadius:"12px", padding:"22px", maxWidth:"520px", width:"100%" }
      },
        React.createElement("h3", {
          style: { fontFamily:"'Playfair Display',serif", fontSize:"19px", color:C.navy, margin:"0 0 3px 0" }
        }, "Report a problem"),
        React.createElement("div", { style: { fontSize:"13px", color:C.navy, fontWeight:600, marginBottom:"2px" } },
          reportingProvider.name),
        React.createElement("p", { style: { fontSize:"11px", color:C.gold, marginBottom:"14px" } },
          "This contact is shared across every firm. Once confirmed, your correction fixes it for all of them."),

        React.createElement("label", { style: { display:"block", fontSize:"11px", fontWeight:600, color:C.navy, marginBottom:"3px" } }, "What's wrong"),
        React.createElement("select", {
          value: reportForm.report_type,
          onChange: e => setReportForm(f => ({ ...f, report_type: e.target.value })),
          style: { width:"100%", padding:"8px 10px", borderRadius:"6px", border:`1px solid ${C.warmGray}`, fontSize:"13px", marginBottom:"10px", background:"#fff" }
        },
          React.createElement("option", { value:"wrong_fax" }, "Fax number is wrong"),
          React.createElement("option", { value:"disconnected" }, "Number is disconnected"),
          React.createElement("option", { value:"wrong_phone" }, "Phone number is wrong"),
          React.createElement("option", { value:"wrong_address" }, "Address is wrong"),
          React.createElement("option", { value:"wrong_email" }, "Email is wrong"),
          React.createElement("option", { value:"wrong_department" }, "Wrong department for this request"),
          React.createElement("option", { value:"moved" }, "They moved"),
          React.createElement("option", { value:"closed" }, "Closed / no longer operating"),
          React.createElement("option", { value:"other" }, "Something else"),
        ),

        reportingProvider.currentFax && React.createElement("div", {
          style: { fontSize:"11px", color:C.textMuted, marginBottom:"8px" }
        }, "Currently on file: ", React.createElement("code", null, reportingProvider.currentFax)),

        React.createElement("label", { style: { display:"block", fontSize:"11px", fontWeight:600, color:C.navy, marginBottom:"3px" } }, "Correct value (if you know it)"),
        React.createElement("input", {
          type:"text", value: reportForm.proposed_value,
          onChange: e => setReportForm(f => ({ ...f, proposed_value: e.target.value })),
          placeholder:"Leave blank if you only know it's wrong",
          style: { width:"100%", padding:"8px 10px", borderRadius:"6px", border:`1px solid ${C.warmGray}`, fontSize:"13px", marginBottom:"10px", boxSizing:"border-box" }
        }),

        React.createElement("label", { style: { display:"block", fontSize:"11px", fontWeight:600, color:C.navy, marginBottom:"3px" } }, "How do you know?"),
        React.createElement("select", {
          value: reportForm.evidence,
          onChange: e => setReportForm(f => ({ ...f, evidence: e.target.value })),
          style: { width:"100%", padding:"8px 10px", borderRadius:"6px", border:`1px solid ${C.warmGray}`, fontSize:"13px", marginBottom:"10px", background:"#fff" }
        },
          React.createElement("option", { value:"fax_failed" }, "Fax failed to send"),
          React.createElement("option", { value:"call_failed" }, "Called — number didn't work"),
          React.createElement("option", { value:"returned_mail" }, "Mail came back"),
          React.createElement("option", { value:"called_them" }, "Called and they told me"),
          React.createElement("option", { value:"staff_confirmed" }, "Their staff confirmed"),
          React.createElement("option", { value:"website" }, "Their website says otherwise"),
          React.createElement("option", { value:"other" }, "Other"),
        ),

        React.createElement("textarea", {
          value: reportForm.detail, rows: 2,
          onChange: e => setReportForm(f => ({ ...f, detail: e.target.value })),
          placeholder:"Anything else worth knowing (optional)",
          style: { width:"100%", padding:"8px 10px", borderRadius:"6px", border:`1px solid ${C.warmGray}`, fontSize:"13px", boxSizing:"border-box", fontFamily:"inherit", resize:"vertical" }
        }),

        React.createElement("div", {
          style: { display:"flex", justifyContent:"flex-end", gap:"8px", marginTop:"14px" }
        },
          React.createElement("button", {
            onClick: () => setReportingProvider(null), disabled: submittingReport,
            style: { padding:"8px 16px", borderRadius:"6px", border:`1px solid ${C.warmGray}`, background:"#fff", color:C.textMuted, fontSize:"13px", cursor:"pointer" }
          }, "Cancel"),
          React.createElement("button", {
            onClick: submitProviderReport, disabled: submittingReport,
            style: { padding:"8px 20px", borderRadius:"6px", border:"none", background:C.gold, color:"#fff", fontSize:"13px", fontWeight:600, cursor:"pointer" }
          }, submittingReport ? "Sending…" : "Submit report"),
        ),
      ),
    ),

    // Provider Directory Modal
    showProviderModal && React.createElement("div", {
      role:"dialog", "aria-modal":"true", "aria-label":"Provider Directory",
      style: {
        position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,0.5)",
        display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000,
      },
      onClick: (e) => { if (e.target === e.currentTarget) setShowProviderModal(false); },
      onKeyDown: e => { if (e.key === "Escape") setShowProviderModal(false); },
    },
      React.createElement("div", {
        style: {
          background:"#fff", borderRadius:"16px", padding:"28px 32px", width:"600px", maxHeight:"80vh",
          display:"flex", flexDirection:"column", boxShadow:"0 20px 60px rgba(0,0,0,0.3)",
        }
      },
        React.createElement("div", { style: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"20px" } },
          React.createElement("h2", { style: { fontFamily:"'Playfair Display',serif", fontSize:"20px", fontWeight:600, color:C.navy } },
            "Add Medical Provider"
          ),
          React.createElement("div", { style: { display:"flex", alignItems:"center", gap:"8px" } },
            React.createElement("button", {
              onClick: handleRefreshProviders,
              disabled: refreshingProviders,
              title: "Refresh from Google Sheets",
              style: {
                border:`1px solid ${C.warmGray}`, background:"#fff", fontSize:"11px", cursor:"pointer",
                color:C.textMuted, padding:"4px 10px", borderRadius:"6px", fontWeight:500,
              }
            }, refreshingProviders ? "Refreshing..." : "\u21bb Refresh"),
            React.createElement("button", {
              onClick: () => setShowProviderModal(false),
              style: { border:"none", background:"none", fontSize:"20px", cursor:"pointer", color:C.textMuted, padding:"4px" }
            }, "\u00d7"),
          ),
        ),
        // Last refreshed indicator
        lastRefreshedAt && React.createElement("div", {
          style: { fontSize:"11px", color:C.textMuted, marginBottom:"10px", textAlign:"right" },
        }, "\u23f0 Last refreshed: " + new Date(lastRefreshedAt).toLocaleString()),
        // Search + Filter
        React.createElement("div", { style: { display:"flex", gap:"10px", marginBottom:"16px", flexWrap:"wrap" } },
          React.createElement("input", {
            type: "text",
            placeholder: "Search providers...",
            value: providerFilter,
            onChange: e => setProviderFilter(e.target.value),
            style: {
              flex:1, minWidth:"160px", padding:"10px 14px", borderRadius:"8px", border:`1px solid ${C.warmGray}`,
              fontSize:"14px", outline:"none",
            }
          }),
          React.createElement("select", {
            value: providerStateFilter,
            onChange: e => setProviderStateFilter(e.target.value),
            style: {
              padding:"10px 14px", borderRadius:"8px", border:`1px solid ${C.warmGray}`,
              fontSize:"14px", background:"#fff", cursor:"pointer", minWidth:"80px",
            }
          },
            React.createElement("option", { value:"" }, "All States"),
            providerStates.map(st =>
              React.createElement("option", { key: st, value: st }, st)
            ),
          ),
          React.createElement("select", {
            value: providerCatFilter,
            onChange: e => setProviderCatFilter(e.target.value),
            style: {
              padding:"10px 14px", borderRadius:"8px", border:`1px solid ${C.warmGray}`,
              fontSize:"14px", background:"#fff", cursor:"pointer", minWidth:"150px",
            }
          },
            React.createElement("option", { value:"" }, "All Categories"),
            providerCategories.map(cat =>
              React.createElement("option", { key: cat, value: cat }, cat)
            ),
          ),
        ),
        // Provider list
        React.createElement("div", { style: { overflowY:"auto", flex:1, maxHeight:"50vh" } },
          providerDirectory
            .filter(p => !providerFilter || p.name.toLowerCase().includes(providerFilter.toLowerCase())
              || (p.facility || "").toLowerCase().includes(providerFilter.toLowerCase())
              || (p.city || "").toLowerCase().includes(providerFilter.toLowerCase()))
            .filter(p => !providerCatFilter || p.category === providerCatFilter)
            .filter(p => !providerStateFilter || p.state === providerStateFilter)
            .map(p => {
              const alreadyAdded = matterProviders.some(mp => mp.provider_id === p.id);
              return React.createElement("div", {
                key: p.id,
                style: {
                  padding:"12px 16px", borderRadius:"8px", border:`1px solid ${C.warmGray}`,
                  marginBottom:"8px", display:"flex", justifyContent:"space-between", alignItems:"center",
                  background: alreadyAdded ? `${C.gold}08` : "#fff",
                  opacity: alreadyAdded ? 0.7 : 1,
                }
              },
                React.createElement("div", { style: { flex:1, minWidth:0 } },
                  React.createElement("div", { style: { fontSize:"14px", fontWeight:600, color:C.navy } }, p.name),
                  React.createElement("div", { style: { fontSize:"11px", color:C.gold, fontWeight:500, marginTop:"1px" } },
                    p.category,
                    (p.city || p.state) ? " \u2014 " + [p.city, p.state].filter(Boolean).join(", ") : ""
                  ),
                  p.preferred_method && React.createElement("div", {
                    style: { fontSize:"11px", color:C.navy, fontWeight:500, marginTop:"2px", background:`${C.gold}15`, display:"inline-block", padding:"1px 8px", borderRadius:"4px" }
                  }, p.preferred_method),
                  p.address && React.createElement("div", { style: { fontSize:"12px", color:C.textMuted, marginTop:"3px" } }, p.address),
                  React.createElement("div", { style: { fontSize:"12px", color:C.textMuted } },
                    [p.phone, p.fax ? "Fax: " + p.fax : "", p.email].filter(Boolean).join(" \u00b7 ")
                  ),
                ),
                React.createElement("div", {
                  style: { display:"flex", alignItems:"center", gap:"8px", flexShrink:0 }
                },
                  // Substep 5-μ.11: report a bad shared contact. Only
                  // offered for rows that came from the universal
                  // directory ("plat-" prefix) — a firm's own custom
                  // entry has nothing to report upstream.
                  String(p.id).startsWith("plat-") && React.createElement("button", {
                    onClick: () => setReportingProvider({
                      entryId: String(p.id).replace("plat-", ""),
                      name: p.name,
                      currentFax: p.fax || "",
                    }),
                    title: "Report wrong contact info — a confirmed fix corrects it for every firm",
                    style: {
                      padding:"5px 9px", borderRadius:"6px",
                      border:`1px solid ${C.warmGray}`, background:"#fff",
                      color:C.textMuted, fontSize:"11px", cursor:"pointer",
                    }
                  }, "Report"),
                  alreadyAdded
                    ? React.createElement("span", { style: { fontSize:"12px", color:C.gold, fontWeight:600 } }, I.check, " Added")
                    : React.createElement("button", {
                        onClick: () => addProviderToMatter(p.id),
                        disabled: addingProvider === p.id,
                        style: {
                          padding:"6px 16px", borderRadius:"8px", border:"none",
                          background:C.navy, color:"#fff", fontSize:"12px", fontWeight:600,
                          cursor:"pointer",
                        }
                      }, addingProvider === p.id ? "Adding..." : "Add"),
                ),
              );
            }),
          providerDirectory.length === 0 && React.createElement("div", {
            style: { textAlign:"center", padding:"32px 16px", color:C.textMuted, fontSize:"13px" }
          }, "No providers loaded. Configure Google Sheets integration or click Refresh."),
        ),
        // Add Custom Provider section
        React.createElement("div", { style: { borderTop:`1px solid ${C.warmGray}`, paddingTop:"16px", marginTop:"16px" } },
          !showCustomProviderForm
            ? React.createElement("button", {
                onClick: () => setShowCustomProviderForm(true),
                style: {
                  width:"100%", padding:"10px 16px", borderRadius:"8px",
                  border:`1px dashed ${C.navy}40`, background:"transparent", cursor:"pointer",
                  fontSize:"13px", fontWeight:600, color:C.navy, display:"flex", alignItems:"center", justifyContent:"center", gap:"6px",
                }
              }, I.plus, " Add Custom Provider")
            : React.createElement("div", null,
                React.createElement("div", { style: { fontSize:"13px", fontWeight:600, color:C.navy, marginBottom:"10px" } }, "Add Custom Provider"),
                React.createElement("input", {
                  type:"text", placeholder:"Provider Name *", value: customProvider.name,
                  onChange: e => setCustomProvider(prev => ({ ...prev, name: e.target.value })),
                  style: { width:"100%", padding:"8px 12px", borderRadius:"6px", border:`1px solid ${C.warmGray}`, fontSize:"13px", marginBottom:"6px" }
                }),
                React.createElement("input", {
                  type:"text", placeholder:"Address", value: customProvider.address,
                  onChange: e => setCustomProvider(prev => ({ ...prev, address: e.target.value })),
                  style: { width:"100%", padding:"8px 12px", borderRadius:"6px", border:`1px solid ${C.warmGray}`, fontSize:"13px", marginBottom:"6px" }
                }),
                React.createElement("div", { style: { display:"flex", gap:"6px", marginBottom:"6px" } },
                  React.createElement("input", {
                    type:"text", placeholder:"Phone", value: customProvider.phone,
                    onChange: e => setCustomProvider(prev => ({ ...prev, phone: e.target.value })),
                    style: { flex:1, padding:"8px 12px", borderRadius:"6px", border:`1px solid ${C.warmGray}`, fontSize:"13px" }
                  }),
                  React.createElement("input", {
                    type:"text", placeholder:"Fax", value: customProvider.fax,
                    onChange: e => setCustomProvider(prev => ({ ...prev, fax: e.target.value })),
                    style: { flex:1, padding:"8px 12px", borderRadius:"6px", border:`1px solid ${C.warmGray}`, fontSize:"13px" }
                  }),
                ),
                React.createElement("input", {
                  type:"text", placeholder:"Email", value: customProvider.email,
                  onChange: e => setCustomProvider(prev => ({ ...prev, email: e.target.value })),
                  style: { width:"100%", padding:"8px 12px", borderRadius:"6px", border:`1px solid ${C.warmGray}`, fontSize:"13px", marginBottom:"6px" }
                }),
                React.createElement("div", { style: { display:"flex", gap:"6px", marginBottom:"8px" } },
                  React.createElement("input", {
                    type:"text", placeholder:"Category (e.g. Hospital)", value: customProvider.category,
                    onChange: e => setCustomProvider(prev => ({ ...prev, category: e.target.value })),
                    style: { flex:1, padding:"8px 12px", borderRadius:"6px", border:`1px solid ${C.warmGray}`, fontSize:"13px" }
                  }),
                  React.createElement("input", {
                    type:"text", placeholder:"State", value: customProvider.state,
                    onChange: e => setCustomProvider(prev => ({ ...prev, state: e.target.value })),
                    style: { width:"80px", padding:"8px 12px", borderRadius:"6px", border:`1px solid ${C.warmGray}`, fontSize:"13px" }
                  }),
                ),
                React.createElement("label", {
                  style: { display:"flex", alignItems:"center", gap:"8px", marginBottom:"10px", fontSize:"12px", color:C.textMuted, cursor:"pointer" }
                },
                  React.createElement("input", {
                    type:"checkbox",
                    checked: customProvider.save_to_directory,
                    onChange: e => setCustomProvider(prev => ({ ...prev, save_to_directory: e.target.checked })),
                    style: { accentColor: C.gold, cursor:"pointer" },
                  }),
                  "Also save to provider directory (Google Sheet)",
                ),
                React.createElement("div", { style: { display:"flex", gap:"8px", justifyContent:"flex-end" } },
                  React.createElement("button", {
                    onClick: () => { setShowCustomProviderForm(false); setCustomProvider({ name:"", address:"", phone:"", fax:"", email:"", category:"", state:"", save_to_directory:false }); },
                    style: { padding:"6px 14px", borderRadius:"6px", border:`1px solid ${C.warmGray}`, background:"#fff", fontSize:"12px", cursor:"pointer", color:C.textMuted }
                  }, "Cancel"),
                  React.createElement("button", {
                    onClick: addCustomProviderToMatter,
                    disabled: !customProvider.name.trim() || addingCustomProvider,
                    style: {
                      padding:"6px 14px", borderRadius:"6px", border:"none",
                      background: customProvider.name.trim() ? C.gold : C.warmGray,
                      color:"#fff", fontSize:"12px", fontWeight:600, cursor: customProvider.name.trim() ? "pointer" : "default",
                    }
                  }, addingCustomProvider ? "Adding..." : "Add Provider"),
                ),
              ),
        ),
      ),
    ),


    // ============ Document Upload & Extraction Card (Documents tab) ============
    React.createElement(TabPanel, { activeTab, ids: "documents" },
    React.createElement("div", {
      style: {
        background:"#fff", borderRadius:"12px", padding:"24px 28px", marginBottom:"20px",
        border: workflowStatus?.intake_summary_generated ? `1px solid ${C.success}30` : `1px solid ${C.warmGray}`,
      }
    },
      React.createElement("div", { style: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px" } },
        React.createElement("div", { style: { display:"flex", alignItems:"center", gap:"10px" } },
          workflowStatus?.intake_summary_generated && React.createElement("div", {
            style: { width:"32px", height:"32px", borderRadius:"50%", background:C.successBg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }
          }, I.check),
          React.createElement("div", null,
            React.createElement("h2", {
              style: { fontSize:"13px", color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:600 }
            }, "Process Incoming Documents"),
            !workflowStatus?.intake_summary_generated && !workflowStatus?.extraction_pushed && React.createElement("div", {
              style: { fontSize:"12px", color:C.textMuted, marginTop:"3px", maxWidth:"560px" }
            }, "Auto-classify images uploaded to this matter (driver's license, insurance card, intake form, wreck photos), rename them, and route to the right folder."),
            workflowStatus?.intake_summary_generated && React.createElement("div", { style: { fontSize:"13px", color:C.success, fontWeight:500 } },
              "Intake summary generated",
              workflowStatus.intake_summary_detail?.date && React.createElement("span", { style: { color:C.textLight, fontWeight:400 } },
                " \u2014 " + new Date(workflowStatus.intake_summary_detail.date).toLocaleDateString()
              ),
            ),
            !workflowStatus?.intake_summary_generated && workflowStatus?.extraction_pushed && React.createElement("div", { style: { fontSize:"13px", color:C.success, fontWeight:500 } },
              "Extraction data pushed to Clio",
              workflowStatus.extraction_detail?.date && React.createElement("span", { style: { color:C.textLight, fontWeight:400 } },
                " \u2014 " + new Date(workflowStatus.extraction_detail.date).toLocaleDateString()
              ),
            ),
          ),
        ),
        React.createElement("div", { style: { display:"flex", alignItems:"center", gap:"8px" } },
          workflowStatus?.intake_summary_generated && React.createElement("span", {
            style: {
              padding:"4px 12px", borderRadius:"9999px", fontSize:"11px", fontWeight:600,
              background:C.successBg, color:C.success, letterSpacing:"0.03em",
            }
          }, "COMPLETE"),
          uploadedDocs.length > 0 && extractStep === "idle" && React.createElement("button", {
            onClick: openPushReview,
            style: {
              padding:"8px 16px", borderRadius:"8px", border:"none",
              background:C.navy, color:"#fff", fontSize:"12px", fontWeight:600, cursor:"pointer",
              display:"flex", alignItems:"center", gap:"6px",
            }
          }, I.zap, " Generate Intake Summary"),
        ),
      ),

      // Processed docs badges
      uploadedDocs.length > 0 && extractStep !== "reviewing" && React.createElement("div", {
        style: { display:"flex", flexWrap:"wrap", gap:"8px", marginBottom:"16px" }
      },
        uploadedDocs.map((doc, i) =>
          React.createElement("span", {
            key: i,
            style: {
              padding:"4px 12px", borderRadius:"9999px", fontSize:"12px", fontWeight:600,
              background:C.successBg, color:C.success, display:"flex", alignItems:"center", gap:"4px",
            }
          }, I.check, " ", DOC_TYPE_LABELS[doc.document_type] || doc.document_type)
        ),
      ),

      // --- Idle state ---------------------------------------------
      // Substep 5-π.4: this was three stacked full-width buttons — a
      // 60px-tall gold slab, an "Or pick specific documents" header,
      // then two more full-width rows. Three walls in a card whose job
      // is one decision: process everything, or choose what to process.
      //
      // Now one row. The bulk action is an ordinary primary button (it
      // does not need to be enormous to be the obvious default — it is
      // the only filled thing here), and the two "pick specific" paths
      // sit beside it as quiet text actions. Drag-and-drop still lands
      // on the upload action, and the whole strip is a drop target so
      // the file has somewhere forgiving to go.
      extractStep === "idle" && React.createElement("div", null,
        React.createElement("div", {
          onDragOver: (e) => { e.preventDefault(); e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.background = `${C.gold}08`; },
          onDragLeave: (e) => { e.currentTarget.style.borderColor = `${C.warmGray}80`; e.currentTarget.style.background = C.warmWhite; },
          onDrop: (e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = `${C.warmGray}80`;
            e.currentTarget.style.background = C.warmWhite;
            const file = e.dataTransfer.files[0];
            if (file) startClassifyUpload(file);
          },
          style: {
            display:"flex", alignItems:"center", gap:"16px", flexWrap:"wrap",
            padding:"14px 16px", borderRadius:"10px",
            background: C.warmWhite, border:`1px dashed ${C.warmGray}80`,
            transition:"border-color 0.15s, background 0.15s",
          },
        },
          React.createElement("button", {
            onClick: handleProcessAll,
            disabled: processingAll,
            style: {
              padding:"10px 20px", borderRadius:"8px", border:"none",
              background: C.gold, color:"#fff", fontSize:"13px", fontWeight:700,
              cursor: processingAll ? "wait" : "pointer", opacity: processingAll ? 0.7 : 1,
              display:"flex", alignItems:"center", gap:"8px", flexShrink:0,
              letterSpacing:"0.02em",
            },
          },
            processingAll ? React.createElement(Spinner, { size: 14, color: "#fff" }) : I.zap,
            processingAll ? "Processing…" : "Process all images",
          ),

          React.createElement("div", { style: { display:"flex", alignItems:"center", gap:"10px", flexWrap:"wrap" } },
            React.createElement("span", { style: { fontSize:"12px", color:C.textLight } }, "or pick specific:"),
            React.createElement("button", {
              onClick: handleOpenClioScan,
              style: {
                border:"none", background:"none", padding:0, cursor:"pointer",
                fontSize:"12px", fontWeight:600, color:C.navy, textDecoration:"underline",
                textUnderlineOffset:"3px",
              },
            }, "from Clio"),
            React.createElement("span", { style: { fontSize:"12px", color:C.textLight } }, "·"),
            React.createElement("button", {
              onClick: handleDocUpload,
              style: {
                border:"none", background:"none", padding:0, cursor:"pointer",
                fontSize:"12px", fontWeight:600, color:C.navy, textDecoration:"underline",
                textUnderlineOffset:"3px",
              },
            }, "from your computer"),
            React.createElement("span", { style: { fontSize:"12px", color:C.textLight } }, "— or drop a file here"),
          ),
        ),

        // Process All result summary
        processAllResult && processAllResult.processed?.length > 0 && React.createElement("div", {
          style: { marginTop:"12px", padding:"12px 16px", borderRadius:"8px", background:C.successBg, border:`1px solid ${C.success}30` }
        },
          React.createElement("div", { style: { fontSize:"13px", fontWeight:600, color:C.success, marginBottom:"6px" } },
            `Processed ${processAllResult.processed.length} document(s)`
          ),
          processAllResult.processed.map((p, i) =>
            React.createElement("div", { key:i, style: { fontSize:"12px", color:C.success, paddingLeft:"8px" } },
              `\u2713 ${p.original_name} \u2192 ${p.suggested_filename}` + (p.moved_to_folder ? ` (${p.moved_to_folder})` : "")
            )
          ),
          processAllResult.intake_summary && React.createElement("div", {
            style: { fontSize:"12px", color:C.success, paddingLeft:"8px", marginTop:"4px", fontWeight:600 }
          }, `\u2713 Intake summary: ${processAllResult.intake_summary.filename}`),
          processAllResult.warnings?.length > 0 && React.createElement("div", { style: { marginTop:"8px" } },
            processAllResult.warnings.map((w, i) =>
              React.createElement("div", { key:i, style: { fontSize:"12px", color:"#92400E", paddingLeft:"8px" } }, `\u26A0 ${w}`)
            )
          ),
        ),
      ),

      // --- Push to Clio Review Modal ---
      showPushReview && React.createElement("div", {
        role:"dialog", "aria-modal":"true", "aria-label":"Review and Push to Clio",
        style: {
          position:"fixed", inset:0, zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center",
          background:"rgba(0,0,0,0.45)", backdropFilter:"blur(2px)",
        },
        onKeyDown: e => { if (e.key === "Escape") setShowPushReview(false); },
      },
        React.createElement("div", {
          className: "slide-down",
          style: {
            background:"#fff", borderRadius:"14px", padding:"28px 32px", width:"100%", maxWidth:"640px",
            boxShadow:"0 20px 60px rgba(0,0,0,0.3)", maxHeight:"85vh", overflow:"auto",
          }
        },
          React.createElement("div", { style: { display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"6px" } },
            React.createElement("h2", {
              style: { fontFamily:"'Playfair Display',serif", fontSize:"20px", fontWeight:600, color:C.navy }
            }, "Review & Push to Clio"),
            React.createElement("button", {
              onClick: () => { setShowPushReview(false); handleGenerateIntakeSummary(); },
              style: { border:"none", background:"none", cursor:"pointer", fontSize:"12px", color:C.textMuted, textDecoration:"underline" }
            }, "Skip & generate summary"),
          ),
          React.createElement("p", {
            style: { fontSize:"13px", color:C.textMuted, marginBottom:"20px", lineHeight:1.5 }
          }, "Compare extracted data with what\u2019s currently in Clio. Choose which values to keep for each field."),

          // Loading
          !clioCurrentData && React.createElement("div", { style: { padding:"32px", textAlign:"center" } },
            React.createElement(Spinner, { size: 28 }),
            React.createElement("div", { style: { fontSize:"13px", color:C.textMuted, marginTop:"12px" } }, "Fetching current Clio data..."),
          ),

          // Comparison table
          clioCurrentData && (() => {
            const extracted = getExtractedData();
            const fields = buildCompareFields(clioCurrentData, extracted);
            if (fields.length === 0) {
              return React.createElement("div", { style: { padding:"20px", textAlign:"center", color:C.textMuted } },
                "No comparable fields found in the extractions."
              );
            }
            return React.createElement(React.Fragment, null,
              // Header
              React.createElement("div", {
                style: { display:"grid", gridTemplateColumns:"140px 1fr 40px 1fr", gap:"8px", padding:"8px 12px", marginBottom:"4px" }
              },
                React.createElement("div", { style: { fontSize:"11px", fontWeight:700, color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.05em" } }, "Field"),
                React.createElement("div", { style: { fontSize:"11px", fontWeight:700, color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.05em" } }, "In Clio"),
                React.createElement("div"),
                React.createElement("div", { style: { fontSize:"11px", fontWeight:700, color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.05em" } }, "Extracted"),
              ),

              // Rows
              React.createElement("div", {
                style: { border:`1px solid ${C.warmGray}`, borderRadius:"8px", overflow:"hidden" }
              },
                fields.map((f, i) =>
                  React.createElement("div", {
                    key: f.key,
                    style: {
                      display:"grid", gridTemplateColumns:"140px 1fr 40px 1fr", gap:"8px", padding:"10px 12px",
                      alignItems:"center",
                      borderBottom: i < fields.length - 1 ? `1px solid ${C.warmGray}30` : "none",
                      background: f.match ? "#fff" : `${C.gold}08`,
                    }
                  },
                    // Label
                    React.createElement("div", { style: { fontSize:"12px", fontWeight:600, color:C.navy } }, f.label),

                    // Clio value (clickable radio)
                    React.createElement("div", {
                      onClick: () => setPushSelections(prev => ({ ...prev, [f.key]: "keep" })),
                      style: {
                        fontSize:"13px", color: f.clio ? C.textDark : C.textLight, cursor:"pointer",
                        padding:"6px 10px", borderRadius:"6px",
                        border: pushSelections[f.key] === "keep" ? `2px solid ${C.navy}` : `1px solid ${C.warmGray}`,
                        background: pushSelections[f.key] === "keep" ? `${C.navy}08` : "#fff",
                        fontStyle: f.clio ? "normal" : "italic",
                      }
                    }, f.clio || "(empty)"),

                    // Match indicator
                    React.createElement("div", { style: { textAlign:"center" } },
                      f.match
                        ? React.createElement("span", { style: { color:C.success, fontSize:"14px" } }, "\u2713")
                        : React.createElement("span", { style: { color:C.gold, fontSize:"14px", fontWeight:700 } }, "\u2260")
                    ),

                    // Extracted value (clickable radio)
                    React.createElement("div", {
                      onClick: () => setPushSelections(prev => ({ ...prev, [f.key]: "extracted" })),
                      style: {
                        fontSize:"13px", color: f.extracted ? C.textDark : C.textLight, cursor:"pointer",
                        padding:"6px 10px", borderRadius:"6px",
                        border: pushSelections[f.key] === "extracted" ? `2px solid ${C.navy}` : `1px solid ${C.warmGray}`,
                        background: pushSelections[f.key] === "extracted" ? `${C.navy}08` : "#fff",
                        fontStyle: f.extracted ? "normal" : "italic",
                      }
                    }, f.extracted || "(empty)"),
                  )
                ),
              ),

              // Legend
              React.createElement("div", {
                style: { display:"flex", gap:"16px", marginTop:"12px", fontSize:"11px", color:C.textMuted }
              },
                React.createElement("span", null, "\u2713 = values match"),
                React.createElement("span", null, "\u2260 = values differ"),
                React.createElement("span", null, "Click a value to select it"),
              ),

              // Action buttons
              React.createElement("div", {
                style: { display:"flex", gap:"10px", justifyContent:"flex-end", marginTop:"20px" }
              },
                React.createElement("button", {
                  onClick: () => { setShowPushReview(false); handleGenerateIntakeSummary(); },
                  style: {
                    padding:"10px 20px", borderRadius:"8px", border:`1px solid ${C.warmGray}`,
                    background:"#fff", cursor:"pointer", fontSize:"13px", fontWeight:600, color:C.textMuted,
                  }
                }, "Skip (Don\u2019t Update Clio)"),
                React.createElement("button", {
                  onClick: handleConfirmPush,
                  disabled: pushingToClio,
                  style: {
                    padding:"10px 20px", borderRadius:"8px", border:"none",
                    background:C.navy, color:"#fff", cursor:"pointer", fontSize:"13px", fontWeight:600,
                    display:"flex", alignItems:"center", gap:"6px", opacity: pushingToClio ? 0.7 : 1,
                  }
                }, pushingToClio ? React.createElement(Spinner, { size:14, color:"#fff" }) : I.upload,
                  pushingToClio ? " Pushing..." : " Update Clio & Generate Summary"),
              ),
            );
          })(),
        ),
      ),


      // --- Low-Confidence Review Modal ---
      confirmUpload && React.createElement("div", {
        role:"dialog", "aria-modal":"true", "aria-label":"Confirm Upload",
        style: {
          position:"fixed", inset:0, zIndex:2100, display:"flex", alignItems:"center", justifyContent:"center",
          background:"rgba(0,0,0,0.5)", backdropFilter:"blur(2px)",
        },
        onClick: e => { if (e.target === e.currentTarget && !committing) cancelConfirmUpload(); },
        onKeyDown: e => { if (e.key === "Escape" && !committing) cancelConfirmUpload(); },
      },
        React.createElement("div", {
          style: {
            background:"#fff", borderRadius:"16px", padding:"24px 28px", width:"460px", maxWidth:"92vw",
            boxShadow:"0 20px 60px rgba(0,0,0,0.3)", display:"flex", flexDirection:"column", gap:"14px",
          }
        },
          React.createElement("div", { style: { display:"flex", justifyContent:"space-between", alignItems:"center" } },
            React.createElement("h2", { style: { fontFamily:"'Playfair Display',serif", fontSize:"18px", fontWeight:600, color:C.navy, margin:0 } },
              "Confirm this upload"
            ),
            React.createElement("div", {
              style: { fontSize:"11px", fontWeight:700, letterSpacing:"0.05em", color:C.gold, background:`${C.gold}18`, padding:"3px 8px", borderRadius:"4px" }
            }, `${confirmUpload.extraction.classification_confidence || 0}% CONFIDENCE`),
          ),
          React.createElement("div", { style: { fontSize:"13px", color:C.textMuted } },
            "The AI wasn't sure what this is. Review the thumbnail, pick the right document type, and give it a name."
          ),
          // Thumbnail
          React.createElement("div", {
            style: {
              width:"100%", background:C.warmWhite, border:`1px solid ${C.warmGray}`, borderRadius:"10px",
              padding:"8px", display:"flex", justifyContent:"center", alignItems:"center", maxHeight:"220px", overflow:"hidden",
            }
          },
            React.createElement("img", {
              src: confirmUpload.thumbnailUrl,
              alt: "Upload preview",
              style: { maxHeight:"200px", maxWidth:"100%", borderRadius:"6px", objectFit:"contain" },
            }),
          ),
          // Doc type
          React.createElement("label", { style: { display:"block", fontSize:"12px", fontWeight:600, color:C.navy } },
            "Document type",
            React.createElement("select", {
              value: confirmUpload.docType,
              onChange: e => setConfirmUpload(prev => prev && ({ ...prev, docType: e.target.value })),
              disabled: committing,
              style: {
                width:"100%", marginTop:"4px", padding:"8px 10px", borderRadius:"8px",
                border:`1px solid ${C.warmGray}`, fontSize:"13px", background:"#fff", cursor:"pointer",
              }
            },
              DOC_TYPE_OPTIONS.map(o =>
                React.createElement("option", { key: o.value, value: o.value }, o.label)
              ),
            ),
          ),
          // Filename
          React.createElement("label", { style: { display:"block", fontSize:"12px", fontWeight:600, color:C.navy } },
            "Filename",
            React.createElement("input", {
              type: "text",
              value: confirmUpload.filename,
              onChange: e => setConfirmUpload(prev => prev && ({ ...prev, filename: e.target.value })),
              disabled: committing,
              style: {
                width:"100%", marginTop:"4px", padding:"8px 10px", borderRadius:"8px",
                border:`1px solid ${C.warmGray}`, fontSize:"13px",
              }
            }),
          ),
          React.createElement("div", { style: { display:"flex", justifyContent:"flex-end", gap:"8px" } },
            React.createElement("button", {
              onClick: cancelConfirmUpload,
              disabled: committing,
              style: { padding:"8px 16px", borderRadius:"8px", border:`1px solid ${C.warmGray}`, background:"#fff", fontSize:"13px", color:C.textMuted, cursor: committing ? "default" : "pointer" }
            }, "Cancel"),
            React.createElement("button", {
              onClick: submitConfirmUpload,
              disabled: committing || !confirmUpload.filename.trim(),
              style: {
                padding:"8px 16px", borderRadius:"8px", border:"none",
                background: committing || !confirmUpload.filename.trim() ? C.warmGray : C.navy,
                color:"#fff", fontSize:"13px", fontWeight:600,
                cursor: committing || !confirmUpload.filename.trim() ? "default" : "pointer",
              }
            }, committing ? "Uploading..." : "Upload to Clio"),
          ),
        ),
      ),

      // --- Clio Scan Modal ---
      showClioScanModal && React.createElement("div", {
        role:"dialog", "aria-modal":"true", "aria-label":"Scan Clio Documents",
        style: {
          position:"fixed", inset:0, zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center",
          background:"rgba(0,0,0,0.45)", backdropFilter:"blur(2px)",
        },
        onClick: e => { if (e.target === e.currentTarget) setShowClioScanModal(false); },
        onKeyDown: e => { if (e.key === "Escape") setShowClioScanModal(false); },
      },
        React.createElement("div", {
          className: "slide-down",
          style: {
            background:"#fff", borderRadius:"14px", padding:"28px 32px", width:"100%", maxWidth:"520px",
            boxShadow:"0 20px 60px rgba(0,0,0,0.3)", maxHeight:"80vh", overflow:"auto",
          }
        },
          React.createElement("div", { style: { display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"6px" } },
            React.createElement("h2", {
              style: { fontFamily:"'Playfair Display',serif", fontSize:"20px", fontWeight:600, color:C.navy }
            }, "Clio Documents"),
            React.createElement("button", {
              onClick: () => setShowClioScanModal(false),
              style: { border:"none", background:"none", cursor:"pointer", fontSize:"20px", color:C.textLight, padding:"0 4px" }
            }, "\u00d7"),
          ),
          React.createElement("p", {
            style: { fontSize:"13px", color:C.textMuted, marginBottom:"16px", lineHeight:1.5 }
          }, "Select image files from this matter to classify and extract fields."),

          // Loading
          scanningClio && React.createElement("div", { style: { padding:"32px", textAlign:"center" } },
            React.createElement(Spinner, { size: 28 }),
            React.createElement("div", { style: { fontSize:"13px", color:C.textMuted, marginTop:"12px" } },
              "Fetching documents from Clio..."
            ),
          ),

          // Empty state
          !scanningClio && clioImages.length === 0 && React.createElement("div", {
            style: { padding:"32px", textAlign:"center" }
          },
            React.createElement("div", { style: { fontSize:"14px", color:C.textMuted, marginBottom:"8px" } },
              "No image files found"
            ),
            React.createElement("div", { style: { fontSize:"12px", color:C.textLight } },
              "This matter has no image documents in Clio. Upload photos using the drop zone above."
            ),
          ),

          // Image list with checkboxes
          !scanningClio && clioImages.length > 0 && React.createElement(React.Fragment, null,
            // Select all toggle
            React.createElement("div", {
              style: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"12px" }
            },
              React.createElement("span", { style: { fontSize:"13px", color:C.textMuted } },
                `${clioImages.length} image${clioImages.length !== 1 ? "s" : ""} found`
              ),
              React.createElement("button", {
                onClick: toggleSelectAll,
                style: {
                  border:"none", background:"none", cursor:"pointer", fontSize:"12px",
                  fontWeight:600, color:C.navy, textDecoration:"underline",
                }
              }, Object.keys(scanSelected).length === clioImages.length ? "Deselect All" : "Select All"),
            ),

            // Document rows
            React.createElement("div", {
              style: { border:`1px solid ${C.warmGray}`, borderRadius:"8px", overflow:"hidden", marginBottom:"16px" }
            },
              clioImages.map((doc, i) =>
                React.createElement("div", {
                  key: doc.id,
                  onClick: () => toggleScanSelect(doc.id),
                  style: {
                    display:"flex", alignItems:"center", gap:"12px", padding:"10px 14px",
                    borderBottom: i < clioImages.length - 1 ? `1px solid ${C.warmGray}30` : "none",
                    background: scanSelected[doc.id] ? `${C.gold}10` : (i % 2 === 0 ? "#fff" : C.warmWhite),
                    cursor:"pointer", transition:"background 0.15s",
                  }
                },
                  // Checkbox
                  React.createElement("div", {
                    style: {
                      width:20, height:20, borderRadius:"4px", flexShrink:0,
                      border: scanSelected[doc.id] ? `2px solid ${C.navy}` : `2px solid ${C.warmGray}`,
                      background: scanSelected[doc.id] ? C.navy : "#fff",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      transition:"all 0.15s",
                    }
                  }, scanSelected[doc.id] && React.createElement("svg", {
                    width:12, height:12, viewBox:"0 0 24 24", fill:"none", stroke:"#fff", strokeWidth:3, strokeLinecap:"round"
                  }, React.createElement("polyline", { points:"20 6 9 17 4 12" }))),

                  // File icon
                  React.createElement("div", {
                    style: { width:32, height:32, borderRadius:"6px", background:`${C.gold}15`, display:"flex", alignItems:"center", justifyContent:"center", color:C.gold, flexShrink:0 }
                  }, I.camera),

                  // File info
                  React.createElement("div", { style: { flex:1, minWidth:0 } },
                    React.createElement("div", {
                      style: { fontSize:"13px", fontWeight:600, color:C.navy, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }
                    }, doc.name),
                    React.createElement("div", { style: { fontSize:"11px", color:C.textMuted } },
                      doc.content_type || "image"
                    ),
                  ),
                )
              ),
            ),

            // Classify Selected button
            React.createElement("div", { style: { display:"flex", justifyContent:"flex-end", gap:"10px" } },
              React.createElement("button", {
                onClick: () => setShowClioScanModal(false),
                style: {
                  padding:"10px 20px", borderRadius:"8px", border:`1px solid ${C.warmGray}`,
                  background:"#fff", cursor:"pointer", fontSize:"13px", fontWeight:600, color:C.textMuted,
                }
              }, "Cancel"),
              React.createElement("button", {
                onClick: handleStartScanQueue,
                disabled: Object.keys(scanSelected).length === 0,
                style: {
                  padding:"10px 20px", borderRadius:"8px", border:"none",
                  background: Object.keys(scanSelected).length === 0 ? C.warmGray : C.navy,
                  color:"#fff", cursor: Object.keys(scanSelected).length === 0 ? "not-allowed" : "pointer",
                  fontSize:"13px", fontWeight:600, display:"flex", alignItems:"center", gap:"6px",
                }
              }, I.zap, ` Classify ${Object.keys(scanSelected).length || ""} Selected`),
            ),
          ),
        ),
      ),

      // --- Uploading state: Spinner ---
      extractStep === "uploading" && React.createElement("div", {
        style: { padding:"40px", textAlign:"center" }
      },
        React.createElement(Spinner, { size: 36 }),
        React.createElement("div", { style: { fontSize:"15px", fontWeight:600, color:C.navy, marginTop:"16px" } },
          scanQueue.length > 0
            ? `Classifying image ${scanIndex + 1} of ${scanQueue.length}...`
            : "Classifying document with AI..."
        ),
        React.createElement("div", { style: { fontSize:"12px", color:C.textMuted, marginTop:"4px" } },
          scanQueue.length > 0
            ? `"${(scanQueue[scanIndex] || {}).name || ""}"`
            : "Detecting document type and extracting fields"
        ),
      ),

      // --- Reviewing state: Editable fields ---
      extractStep === "reviewing" && currentExtraction && React.createElement("div", null,
        // Document type badge
        React.createElement("div", { style: { display:"flex", alignItems:"center", gap:"10px", marginBottom:"16px" } },
          React.createElement("span", {
            style: {
              padding:"4px 14px", borderRadius:"9999px", fontSize:"13px", fontWeight:700,
              background:`${C.navy}12`, color:C.navy,
            }
          }, DOC_TYPE_LABELS[currentExtraction.document_type] || currentExtraction.document_type),
          React.createElement("span", { style: { fontSize:"12px", color:C.textMuted } },
            `Classification confidence: ${currentExtraction.classification_confidence}%`
          ),
          currentExtraction.clio_upload && currentExtraction.clio_upload.document_id && React.createElement("span", {
            style: { fontSize:"11px", color:C.success, fontWeight:500 }
          }, I.check, " Image uploaded to Clio"),
          currentExtraction.mock && React.createElement(Badge, { variant:"gold" }, "Demo data"),
        ),

        // Suggested filename
        currentExtraction.suggested_filename && React.createElement("div", {
          style: { fontSize:"12px", color:C.textMuted, marginBottom:"12px" }
        }, "Saved as: ", React.createElement("strong", null, currentExtraction.suggested_filename)),

        // Editable field rows
        React.createElement("div", {
          style: { border:`1px solid ${C.warmGray}`, borderRadius:"8px", overflow:"hidden" }
        },
          currentExtraction.fields.map((field, i) =>
            React.createElement("div", {
              key: i,
              style: {
                display:"grid", gridTemplateColumns:"160px 1fr 55px", padding:"10px 16px",
                alignItems:"center",
                borderBottom: i < currentExtraction.fields.length - 1 ? `1px solid ${C.warmGray}30` : "none",
                background: i % 2 === 0 ? "#fff" : C.warmWhite,
              }
            },
              React.createElement("div", { style: { fontSize:"12px", color:C.textMuted, fontWeight:500 } },
                field.name.replace(/_/g, " ")
              ),
              React.createElement("input", {
                defaultValue: field.value,
                onChange: (e) => {
                  const val = e.target.value;
                  setCurrentExtraction(prev => {
                    const newFields = [...prev.fields];
                    newFields[i] = { ...newFields[i], value: val };
                    const newRaw = { ...prev.raw_fields, [field.name]: val };
                    return { ...prev, fields: newFields, raw_fields: newRaw };
                  });
                },
                style: {
                  border:`1px solid ${C.warmGray}`, borderRadius:"6px", padding:"6px 10px",
                  fontSize:"13px", color:C.navy, fontWeight:500, width:"100%",
                }
              }),
              React.createElement("div", {
                style: { textAlign:"right", fontSize:"12px", fontWeight:600, color: getConfidenceColor(field.confidence) }
              }, field.confidence + "%"),
            )
          ),
        ),

        // Scan progress indicator
        scanQueue.length > 0 && React.createElement("div", {
          style: { fontSize:"12px", color:C.textMuted, marginTop:"12px", marginBottom:"4px" }
        }, `Processing ${scanIndex + 1} of ${scanQueue.length} images...`),

        // Intake-form-specific action: resolve providers listed by the client
        currentExtraction.document_type === "INTAKE_FORM"
          && parseIntakeProviders(currentExtraction).length > 0
          && React.createElement("div", {
            style: {
              marginTop:"12px", padding:"12px 14px", borderRadius:"8px",
              background:`${C.gold}10`, border:`1px solid ${C.gold}40`,
              display:"flex", alignItems:"center", justifyContent:"space-between", gap:"12px",
            }
          },
            React.createElement("div", null,
              React.createElement("div", { style: { fontSize:"13px", fontWeight:600, color:C.navy } },
                `Found ${parseIntakeProviders(currentExtraction).length} medical provider(s) on this intake form`
              ),
              React.createElement("div", { style: { fontSize:"11px", color:C.textMuted, marginTop:"2px" } },
                "Review, edit, and add them to the matter without leaving this page."
              ),
            ),
            React.createElement("button", {
              onClick: () => openIntakeProviderReview(currentExtraction),
              disabled: !!intakeReview,
              style: {
                padding:"8px 16px", borderRadius:"8px", border:"none",
                background: intakeReview ? C.warmGray : C.gold, color:"#fff",
                fontSize:"12px", fontWeight:600, cursor: intakeReview ? "default" : "pointer",
                whiteSpace:"nowrap",
              }
            }, intakeReview ? "Opened" : "Review providers"),
          ),

        // Action buttons
        React.createElement("div", {
          style: { display:"flex", gap:"10px", justifyContent:"flex-end", marginTop:"16px" }
        },
          React.createElement("button", {
            onClick: scanQueue.length > 0 ? handleDiscardScanExtraction : handleDiscardExtraction,
            style: {
              padding:"10px 20px", borderRadius:"8px", border:`1px solid ${C.warmGray}`,
              background:"#fff", cursor:"pointer", fontSize:"13px", fontWeight:600, color:C.textMuted,
            }
          }, scanQueue.length > 0 ? "Skip" : "Discard"),
          React.createElement("button", {
            onClick: scanQueue.length > 0 ? handleAcceptScanExtraction : handleAcceptExtraction,
            style: {
              padding:"10px 20px", borderRadius:"8px", border:"none",
              background:C.navy, color:"#fff", cursor:"pointer", fontSize:"13px", fontWeight:600,
              display:"flex", alignItems:"center", gap:"6px",
            }
          }, I.check, scanQueue.length > 0
            ? (scanIndex < scanQueue.length - 1 ? " Accept & Next" : " Accept & Finish")
            : " Accept & Continue"),
        ),
      ),

      // --- Summary state: Generating intake summary ---
      extractStep === "summary" && React.createElement("div", {
        style: { padding:"40px", textAlign:"center" }
      },
        generatingSummary
          ? React.createElement(React.Fragment, null,
              React.createElement(Spinner, { size: 36 }),
              React.createElement("div", { style: { fontSize:"15px", fontWeight:600, color:C.navy, marginTop:"16px" } },
                "Generating Client Intake Summary..."
              ),
              React.createElement("div", { style: { fontSize:"12px", color:C.textMuted, marginTop:"4px" } },
                "Merging extracted data and uploading to Clio"
              ),
            )
          : React.createElement(React.Fragment, null,
              React.createElement("div", {
                style: { width:48, height:48, borderRadius:"50%", background:C.successBg, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 12px", color:C.success }
              }, I.check),
              React.createElement("div", { style: { fontSize:"15px", fontWeight:600, color:C.navy } },
                "Intake Summary Generated"
              ),
              React.createElement("div", { style: { fontSize:"12px", color:C.textMuted, marginTop:"4px" } },
                "Document has been uploaded to Clio"
              ),
            ),
      ),
    )),


    // --- Intake Form → Provider Review Modal ---
    // Rendered at the TOP level (outside any tab conditional) so the
    // modal appears regardless of which tab is active when triggered.
    // The "Scan intake form" button on the Providers tab and the
    // process-all flow on the Documents tab both set intakeReview.
    intakeReview && React.createElement("div", {
      role:"dialog", "aria-modal":"true", "aria-label":"Review Medical Providers from Intake",
      style: {
        position:"fixed", inset:0, zIndex:2100, display:"flex", alignItems:"center", justifyContent:"center",
        background:"rgba(0,0,0,0.5)", backdropFilter:"blur(2px)",
      },
      onClick: e => {
        if (e.target === e.currentTarget && !intakeReview.confirming) setIntakeReview(null);
      },
      onKeyDown: e => { if (e.key === "Escape" && !intakeReview.confirming) setIntakeReview(null); },
    },
      React.createElement("div", {
        style: {
          background:"#fff", borderRadius:"16px", padding:"24px 28px", width:"720px", maxWidth:"94vw",
          maxHeight:"86vh", boxShadow:"0 20px 60px rgba(0,0,0,0.3)",
          display:"flex", flexDirection:"column", gap:"12px",
        }
      },
        React.createElement("div", { style: { display:"flex", justifyContent:"space-between", alignItems:"flex-start" } },
          React.createElement("div", null,
            React.createElement("h2", { style: { fontFamily:"'Playfair Display',serif", fontSize:"20px", fontWeight:600, color:C.navy, margin:0 } },
              "Medical providers from intake form"
            ),
            React.createElement("div", { style: { fontSize:"12px", color:C.textMuted, marginTop:"2px" } },
              "Matched entries come from the directory. Unmatched entries are web-search guesses — verify before approving."
            ),
          ),
          React.createElement("button", {
            onClick: () => !intakeReview.confirming && setIntakeReview(null),
            disabled: intakeReview.confirming,
            style: { border:"none", background:"none", fontSize:"20px", cursor: intakeReview.confirming ? "default" : "pointer", color:C.textMuted, padding:"4px" }
          }, "\u00d7"),
        ),
        intakeReview.resolving
          ? React.createElement("div", { style: { padding:"40px", textAlign:"center" } },
              React.createElement(Spinner, { size: 36 }),
              React.createElement("div", { style: { fontSize:"13px", color:C.textMuted, marginTop:"12px" } },
                "Matching against the directory and searching the web..."
              ),
            )
          : React.createElement("div", { style: { flex:1, overflowY:"auto", display:"flex", flexDirection:"column", gap:"12px", paddingRight:"4px" } },
              intakeReview.entries.length === 0 && React.createElement("div", {
                style: { padding:"20px", textAlign:"center", fontSize:"13px", color:C.textMuted }
              }, "No providers resolved."),
              intakeReview.entries.map((e, idx) => {
                const isDir = e.source === "directory";
                const badgeColor = isDir ? C.success : (e.confidence === "high" ? C.gold : C.textLight);
                const badgeLabel = isDir ? "FROM DIRECTORY" :
                  (e.confidence === "high" ? "WEB — HIGH CONFIDENCE" :
                   e.confidence === "medium" ? "WEB — MEDIUM" : "WEB — LOW / VERIFY");
                return React.createElement("div", {
                  key: idx,
                  style: {
                    border: `1px solid ${e.include ? C.gold : C.warmGray}`,
                    background: e.include ? `${C.gold}06` : "#fff",
                    borderRadius:"10px", padding:"12px 14px",
                  }
                },
                  React.createElement("div", { style: { display:"flex", alignItems:"center", gap:"10px", marginBottom:"10px" } },
                    React.createElement("input", {
                      type:"checkbox", checked: !!e.include,
                      onChange: ev => updateIntakeEntry(idx, { include: ev.target.checked }),
                      style: { accentColor: C.gold, cursor:"pointer", width:16, height:16 },
                    }),
                    React.createElement("div", { style: { flex:1, minWidth:0 } },
                      React.createElement("div", { style: { fontSize:"11px", color:C.textMuted } },
                        `From intake: "${e.extracted_name}"` + (e.extracted_location ? ` — ${e.extracted_location}` : "")
                      ),
                    ),
                    React.createElement("span", {
                      style: {
                        fontSize:"10px", fontWeight:700, letterSpacing:"0.05em",
                        color:"#fff", background: badgeColor, padding:"3px 7px", borderRadius:"4px",
                      }
                    }, badgeLabel),
                  ),
                  React.createElement("div", {
                    style: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px" }
                  },
                    ["name","category","address","city","state","fax","phone","email"].map(f =>
                      React.createElement("label", { key:f, style: { display:"block", fontSize:"11px", color:C.textMuted, fontWeight:500 } },
                        f.replace(/^./, c => c.toUpperCase()),
                        React.createElement("input", {
                          type:"text", value: e[f] || "",
                          onChange: ev => updateIntakeEntry(idx, { [f]: ev.target.value }),
                          style: {
                            width:"100%", marginTop:"2px", padding:"6px 10px", fontSize:"13px",
                            borderRadius:"6px", border:`1px solid ${C.warmGray}`,
                          }
                        }),
                      )
                    ),
                  ),
                  React.createElement("div", { style: { marginTop:"8px", display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:"10px", flexWrap:"wrap" } },
                    React.createElement("div", { style: { fontSize:"11px", color:C.textLight, fontStyle:"italic", flex:1, minWidth:"180px" } },
                      e.notes || (isDir ? "" : "")
                    ),
                    // Three independent destination toggles. "Include on
                    // matter" is the include checkbox above; these two
                    // are the additional destinations.
                    React.createElement("div", { style: { display:"flex", flexDirection:"column", gap:"4px", flexShrink:0 } },
                      React.createElement("label", {
                        style: { display:"flex", alignItems:"center", gap:"6px", fontSize:"11px", color:C.textMuted, cursor:"pointer", whiteSpace:"nowrap" }
                      },
                        React.createElement("input", {
                          type:"checkbox", checked: e.add_to_clio !== false,
                          onChange: ev => updateIntakeEntry(idx, { add_to_clio: ev.target.checked }),
                          style: { accentColor: C.gold, cursor:"pointer" },
                        }),
                        isDir ? "Update Clio contact" : "Create as Clio contact",
                      ),
                      !isDir && React.createElement("label", {
                        style: { display:"flex", alignItems:"center", gap:"6px", fontSize:"11px", color:C.textMuted, cursor:"pointer", whiteSpace:"nowrap" }
                      },
                        React.createElement("input", {
                          type:"checkbox", checked: !!e.save_to_directory,
                          onChange: ev => updateIntakeEntry(idx, { save_to_directory: ev.target.checked }),
                          style: { accentColor: C.gold, cursor:"pointer" },
                        }),
                        "Also save to directory sheet",
                      ),
                    ),
                  ),
                );
              }),
            ),
        React.createElement("div", {
          style: { display:"flex", justifyContent:"space-between", alignItems:"center", borderTop:`1px solid ${C.warmGray}`, paddingTop:"12px", marginTop:"4px" }
        },
          React.createElement("div", { style: { fontSize:"12px", color:C.textMuted } },
            intakeReview.resolving ? "" :
              `${intakeReview.entries.filter(e => e.include).length} of ${intakeReview.entries.length} selected`
          ),
          React.createElement("div", { style: { display:"flex", gap:"8px" } },
            React.createElement("button", {
              onClick: () => !intakeReview.confirming && setIntakeReview(null),
              disabled: intakeReview.confirming,
              style: { padding:"8px 16px", borderRadius:"8px", border:`1px solid ${C.warmGray}`, background:"#fff", fontSize:"13px", color:C.textMuted, cursor: intakeReview.confirming ? "default" : "pointer" }
            }, "Cancel"),
            React.createElement("button", {
              onClick: confirmIntakeProviders,
              disabled: intakeReview.confirming || intakeReview.resolving,
              style: {
                padding:"8px 16px", borderRadius:"8px", border:"none",
                background: intakeReview.confirming || intakeReview.resolving ? C.warmGray : C.navy,
                color:"#fff", fontSize:"13px", fontWeight:600,
                cursor: intakeReview.confirming || intakeReview.resolving ? "default" : "pointer",
              }
            }, intakeReview.confirming ? "Adding..." : "Approve and add"),
          ),
        ),
      ),
    ),
  );
}

